import React, { useEffect, useMemo, useState, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import RSVPInterface from './RSVPInterface';
import EmailComposerModal from './EmailComposerModal';

// Timezone formatting utility
const formatInHOATimezone = (isoDateTime, tzName, options = {}) => {
  if (!isoDateTime) return '';
  try {
    const dt = new Date(isoDateTime);
    const formatOptions = {
      timeZone: tzName || 'America/Chicago',
      hour: 'numeric',
      minute: '2-digit',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...options
    };
    if (options.showZone) {
      formatOptions.timeZoneName = 'short'; // Shows "CT", "ET", etc
    }
    return dt.toLocaleString('en-US', formatOptions);
  } catch (error) {
    console.error('Error formatting time in timezone:', error);
    return new Date(isoDateTime).toLocaleString();
  }
};

const formatTimeOnly = (isoDateTime, tzName) => {
  if (!isoDateTime) return '';
  try {
    const dt = new Date(isoDateTime);
    // When using start_local (already converted), the timezone offset is baked in
    // Just format it and add the timezone name
    const timeStr = dt.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
    
    // Get timezone abbreviation from the ISO string if it has offset info
    // OR use the provided tzName to get abbreviation
    const tzAbbrev = dt.toLocaleString('en-US', {
      timeZone: tzName || 'America/Chicago',
      timeZoneName: 'short'
    }).split(' ').pop(); // Get just the "CDT" or "CST" part
    
    return `${timeStr} ${tzAbbrev}`;
  } catch (error) {
    console.error('Error formatting time:', error);
    return new Date(isoDateTime).toLocaleTimeString();
  }
};

// Mobile Events List Component - Fallback for phone screens
const MobileEventsList = ({ events, onEventClick }) => {
  const [selectedMonth, setSelectedMonth] = React.useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear());
  const [showMonthPicker, setShowMonthPicker] = React.useState(false);

  if (!events || events.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg">
        <p className="text-lg">📅 No upcoming events</p>
        <p className="text-sm mt-2">Check back soon for community events!</p>
      </div>
    );
  }

  // Filter events by selected month/year - use start_local for accurate date matching
  const filteredEvents = events.filter(e => {
    const startDateStr = e.start_local || e.start_datetime || e.start;
    const start = new Date(startDateStr);
    return start.getMonth() === selectedMonth && start.getFullYear() === selectedYear;
  });

  // Sort by start date
  const sorted = [...filteredEvents].sort((a, b) => {
    const da = new Date(a.start_local || a.start_datetime || a.start);
    const db = new Date(b.start_local || b.start_datetime || b.start);
    return da.getTime() - db.getTime();
  });

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];

  const goToPrevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  return (
    <div>
      {/* Month Navigation */}
      <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <button
            onClick={goToPrevMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="relative">
            <button
              onClick={() => setShowMonthPicker(!showMonthPicker)}
              className="px-4 py-2 text-lg font-semibold text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
            >
              {monthNames[selectedMonth]} {selectedYear} ▾
            </button>

            {showMonthPicker && (
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50 w-64">
                <div className="grid grid-cols-3 gap-2 p-3">
                  {monthNames.map((month, idx) => (
                    <button
                      key={month}
                      onClick={() => {
                        setSelectedMonth(idx);
                        setShowMonthPicker(false);
                      }}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        idx === selectedMonth
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {month.substring(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={goToNextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className="mt-2 text-center text-sm text-gray-500">
          {sorted.length} {sorted.length === 1 ? 'event' : 'events'} this month
        </div>
      </div>

      {/* Events List */}
      {sorted.length === 0 ? (
        <div className="bg-white rounded-lg shadow-lg p-8 text-center text-gray-500">
          <p className="text-lg">📅 No events in {monthNames[selectedMonth]}</p>
          <p className="text-sm mt-2">Check other months for upcoming events</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-lg divide-y divide-gray-200">
          {sorted.map((e) => {
            // Use start_local for accurate date display in HOA timezone
            const startStr = e.start_local || e.start_datetime || e.start;
            const start = new Date(startStr);
            const endStr = e.end_local || e.end_datetime || e.end;
            const end = endStr ? new Date(endStr) : null;
            const isPast = start < new Date();

            return (
              <div
                key={e.id || e._id}
                className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                  isPast ? 'opacity-60' : ''
                }`}
                onClick={() => onEventClick && onEventClick(e)}
              >
                <div className="flex items-start space-x-3">
                  <div className="text-2xl flex-shrink-0">
                    {e.event_type === 'Board Meeting' && '📋'}
                    {e.event_type === 'Pool Party' && '🏊'}
                    {e.event_type === 'Community Event' && '🎉'}
                    {!['Board Meeting', 'Pool Party', 'Community Event'].includes(e.event_type) && '📅'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {e.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      📅 {start.toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </p>
                    <p className="text-sm text-gray-600">
                      🕐 {start.toLocaleTimeString('en-US', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                      {end && ` – ${end.toLocaleTimeString('en-US', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}`}
                    </p>
                    {e.location && (
                      <p className="text-sm text-gray-600">
                        📍 {e.location}
                      </p>
                    )}
                    {e.event_type && (
                      <span className="inline-block mt-2 text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                        {e.event_type}
                      </span>
                    )}
                    {isPast && (
                      <div className="text-xs text-gray-400 italic">Past event</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// Event taxonomy with stock photo mapping
export const EVENT_TYPES = [
  'Board Meeting', 'Maintenance', 'ARC Meeting', 'Pool Party', 'Community Event',
  'Holiday Event', 'Emergency Meeting', 'Social Meeting', 'Workshop', 'Fundraiser', 'Other'
];

export const EVENT_TYPE_MAPPING = {
  'Board Meeting': 'board_meeting',
  'Maintenance': 'maintenance', 
  'ARC Meeting': 'arc_meeting',
  'Pool Party': 'pool_party',
  'Community Event': 'community_event',
  'Holiday Event': 'holiday_event',
  'Emergency Meeting': 'emergency_meeting',
  'Social Meeting': 'social_meeting',
  'Workshop': 'workshop',
  'Fundraiser': 'fundraiser',
  'Other': 'other'
};

// Reverse mapping for getting display names from enum values
export const REVERSE_EVENT_TYPE_MAPPING = {
  'board_meeting': 'Board Meeting',
  'maintenance': 'Maintenance',
  'arc_meeting': 'ARC Meeting',
  'pool_party': 'Pool Party',
  'community_event': 'Community Event',
  'holiday_event': 'Holiday Event',
  'emergency_meeting': 'Emergency Meeting',
  'social_meeting': 'Social Meeting',
  'workshop': 'Workshop',
  'fundraiser': 'Fundraiser',
  'other': 'Other'
};
const STOCK_EVENT_COVERS = {
  'board_meeting': 'board_meeting.png',
  'maintenance': 'maintenance.png',
  'arc_meeting': 'arc_meeting.png',
  'pool_party': 'pool_party.png',
  'community_event': 'community_event.png',
  'holiday_event': 'holiday_event.png',
  'emergency_meeting': 'emergency_meeting.png',
  'social_meeting': 'social_meeting.png',
  'workshop': 'workshop.png',
  'fundraiser': 'fundraiser.png',
  'other': 'other.png'
};

export const getEventCover = (eventType, customUrl) => {
  if (customUrl) return customUrl;
  const mappedType = EVENT_TYPE_MAPPING[eventType] || eventType || 'other';
  const photoName = STOCK_EVENT_COVERS[mappedType] || STOCK_EVENT_COVERS.other;
  return `/assets/events/stock/${photoName}`;
};

// Role helper - Updated to use dynamic permissions for events
function useIsBoard() {
  const { user } = useAuth();
  const role = user?.role?.toLowerCase() || '';
  return ['board_member', 'president', 'vice_president', 'treasurer', 'secretary', 'platform_admin'].includes(role);
}

// Custom hook for event-specific permissions
function useEventPermissions() {
  const { user } = useAuth();
  const [eventPermissions, setEventPermissions] = useState({
    canCreate: false,
    canManageAny: false,
    loading: true
  });

  useEffect(() => {
    const checkEventPermissions = async () => {
      if (!user?.id) {
        setEventPermissions({ canCreate: false, canManageAny: false, loading: false });
        return;
      }

      try {
        // Check if user is board member (always has permissions)
        const isBoardMember = ['board_member', 'president', 'vice_president', 'treasurer', 'secretary', 'platform_admin'].includes(user.role?.toLowerCase() || '');
        
        if (isBoardMember) {
          setEventPermissions({ canCreate: true, canManageAny: true, loading: false });
          return;
        }

        // Check calendar settings for specific permissions
        const response = await api.get('/api/hoa-settings');
        const calendarSettings = response.data?.hoa_settings?.calendar || {};
        
        console.log('🔍 PERMISSION CHECK DEBUG:');
        console.log('  Full response:', response.data);
        console.log('  Calendar settings:', calendarSettings);
        console.log('  board_only_create:', calendarSettings.board_only_create);
        console.log('  allowed_individuals:', calendarSettings.allowed_individuals);
        
        let canCreate = false;
        
        // If board_only_create is false, all HOA members can create
        if (calendarSettings.board_only_create === false) {
          console.log('  ✅ All HOA members can create (board_only_create = false)');
          canCreate = true;
        } else {
          console.log('  🔍 Board + specific persons mode, checking allowed individuals...');
          // Check if user is in allowed individuals list
          const allowedIndividuals = calendarSettings.allowed_individuals || [];
          console.log('  Checking event permissions for user:', user.id, user.email);
          console.log('  Allowed individuals:', allowedIndividuals);
          
          canCreate = allowedIndividuals.some(person => {
            // Support different formats:
            if (typeof person === 'string') {
              // String format: could be email or user ID
              const matches = person === user.id || person === user.email;
              console.log(`    Checking string "${person}" against user: ${matches}`);
              return matches;
            } else if (typeof person === 'object' && person !== null) {
              // Object format: check id, email, and name fields
              const matches = person.id === user.id || 
                     person.email === user.email || 
                     person.id === user.email;  // For cases where email was used as ID
              console.log(`    Checking object ${JSON.stringify(person)} against user: ${matches}`);
              return matches;
            }
            return false;
          });
          
          console.log('  Final permission result:', canCreate);
        }

        setEventPermissions({
          canCreate,
          canManageAny: isBoardMember, // Only board members can manage any event
          loading: false
        });
      } catch (error) {
        console.error('Error checking event permissions:', error);
        // Default to board member check only
        const isBoardMember = ['board_member', 'president', 'vice_president', 'treasurer', 'secretary', 'platform_admin'].includes(user.role?.toLowerCase() || '');
        setEventPermissions({ canCreate: isBoardMember, canManageAny: isBoardMember, loading: false });
      }
    };

    checkEventPermissions();
  }, [user?.id, user?.role]);

  const canManageEvent = (event) => {
    // Board members can manage any event
    if (eventPermissions.canManageAny) return true;
    // Event creators can manage their own events
    if (event?.created_by === user?.id) return true;
    return false;
  };

  return {
    canCreateEvents: eventPermissions.canCreate,
    canManageEvent,
    loading: eventPermissions.loading
  };
}

// Image processing function
async function processImage(file, logoUrl = '/hoa-logo-icon.png') {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const maxW = 1600, maxH = 900;
      const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      
      // Draw main image
      ctx.drawImage(img, 0, 0, w, h);
      
      // Add watermark (small logo in bottom-right)
      const watermark = new Image();
      watermark.onload = () => {
        const wmW = Math.max(36, Math.round(w * 0.08));
        const wmH = Math.round(wmW * (watermark.height / watermark.width));
        ctx.globalAlpha = 0.6;
        ctx.drawImage(watermark, w - wmW - 10, h - wmH - 10, wmW, wmH);
        ctx.globalAlpha = 1;
        
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.82);
      };
      watermark.src = logoUrl;
    };
    img.src = URL.createObjectURL(file);
  });
}

export default function CommunityEvents() {
  const isBoard = useIsBoard();
  const { canCreateEvents, canManageEvent, loading: permissionsLoading } = useEventPermissions();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [active, setActive] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthlyHeaders, setMonthlyHeaders] = useState({}); // Store custom monthly headers
  const [calendarApi, setCalendarApi] = useState(null); // Store FullCalendar API reference
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [dayEventsModal, setDayEventsModal] = useState({ show: false, date: null, events: [] });
  const [rsvpCounts, setRsvpCounts] = useState({}); // Cache for RSVP counts
  const [emailSettings, setEmailSettings] = useState({ use_in_app_composer: false, enable_event_emails: true });
  const [emailComposerModal, setEmailComposerModal] = useState({ isOpen: false, event: null, attendees: [] });
  const monthDropdownRef = useRef(null);
  const calendarRef = useRef(null); // Wrapper div ref for layout bump
  const fcRef = useRef(null); // FullCalendar component ref for API access
  
  // Google Calendar sync state
  const [googleSyncEnabled, setGoogleSyncEnabled] = useState(false);
  const [syncingToGoogle, setSyncingToGoogle] = useState(false);
  const [checkingGoogleSync, setCheckingGoogleSync] = useState(true);
  
  // Apple Calendar ICS feed URL
  const appleCalendarUrl = `${process.env.REACT_APP_BACKEND_URL}/api/calendar/ics/${user?.hoa_id || 'default'}`;
  
  // Detect mobile - use state so it's reactive
  const [isMobile, setIsMobile] = useState(false);
  const [isPhoneScreen, setIsPhoneScreen] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
      setIsPhoneScreen(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Force layout recalculation for iOS Safari
  useEffect(() => {
    const bump = () => {
      const api = fcRef.current?.getApi();
      if (api) {
        try {
          api.updateSize();
          console.log('[iOS Safari] Calendar size updated');
        } catch (err) {
          console.error('FullCalendar bump/updateSize failed:', err);
        }
      }
    };
    
    // Run on mount
    requestAnimationFrame(bump);
    setTimeout(bump, 0);
    
    window.addEventListener('orientationchange', bump);
    return () => {
      window.removeEventListener('orientationchange', bump);
    };
  }, []);
  
  // Keep active event in sync with events array (for RSVP updates)
  useEffect(() => {
    if (active) {
      const activeId = active.id || active._id;
      const updatedEvent = events.find(e => (e.id || e._id) === activeId);
      if (updatedEvent && JSON.stringify(updatedEvent) !== JSON.stringify(active)) {
        setActive(updatedEvent);
      }
    }
  }, [events, active]);

  // Load email settings
  useEffect(() => {
    const loadEmailSettings = async () => {
      try {
        const response = await api.get('/api/event-email-settings');
        if (response.data?.success) {
          setEmailSettings({
            use_in_app_composer: response.data.use_in_app_composer,
            enable_event_emails: response.data.enable_event_emails
          });
          console.log('📧 Email settings loaded:', {
            use_in_app_composer: response.data.use_in_app_composer,
            enable_event_emails: response.data.enable_event_emails
          });
        }
      } catch (error) {
        console.error('Error loading email settings:', error);
      }
    };

    // Load settings initially
    loadEmailSettings();

    // Refresh settings when window gains focus (user comes back from admin dashboard)
    const handleFocus = () => {
      console.log('🔄 Window focused, refreshing email settings...');
      loadEmailSettings();
    };

    window.addEventListener('focus', handleFocus);
    
    // Cleanup event listener
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Handle window resize only
  useEffect(() => {
    const handleResize = () => {
      const api = fcRef.current?.getApi();
      if (api) {
        api.updateSize();
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Force FullCalendar to recalc size whenever events change (important for iOS Safari)
  useEffect(() => {
    const api = fcRef.current?.getApi();
    if (!api) return;

    // Small delay so DOM/layout settle after React re-render
    const timer = setTimeout(() => {
      try {
        api.updateSize();
        console.log('[Calendar] Size updated after events change');
      } catch (err) {
        console.error('FullCalendar updateSize failed after events change:', err);
      }
    }, 80);

    return () => clearTimeout(timer);
  }, [events.length]);

  // Check if Google Calendar is connected
  useEffect(() => {
    const checkGoogleSync = async () => {
      try {
        const { data } = await api.get('/api/user/profile');
        // Check if user has Google Calendar tokens
        setGoogleSyncEnabled(!!data.google_calendar_connected);
      } catch (error) {
        console.error('Error checking Google sync status:', error);
        setGoogleSyncEnabled(false);
      } finally {
        setCheckingGoogleSync(false);
      }
    };
    
    if (user) {
      checkGoogleSync();
    }
  }, [user]);

  // Connect Google Calendar
  const connectGoogleCalendar = async () => {
    try {
      const { data } = await api.get('/api/calendar/google/auth');
      
      if (!data.auth_url) {
        alert('Failed to get Google authorization URL');
        return;
      }
      
      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      const popup = window.open(
        data.auth_url,
        'Google Calendar Authorization',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      
      // Listen for success message from popup
      const messageHandler = (event) => {
        if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
          setGoogleSyncEnabled(true);
          alert('✅ Google Calendar connected successfully! You can now sync your events.');
          if (popup && !popup.closed) {
            popup.close();
          }
          window.removeEventListener('message', messageHandler);
        } else if (event.data.type === 'GOOGLE_AUTH_ERROR') {
          alert(`❌ Failed to connect: ${event.data.error}`);
          window.removeEventListener('message', messageHandler);
        }
      };
      
      window.addEventListener('message', messageHandler);
      
      // Clean up if popup is closed manually
      const checkPopup = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(checkPopup);
          window.removeEventListener('message', messageHandler);
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error connecting Google Calendar:', error);
      alert('❌ Failed to connect Google Calendar. Please try again.');
    }
  };

  // Sync events to Google Calendar
  const syncToGoogleCalendar = async () => {
    setSyncingToGoogle(true);
    try {
      const { data } = await api.post('/api/calendar/google/sync');
      
      if (data.success) {
        alert(`✅ Successfully synced ${data.synced_count} events to Google Calendar!\n\nCreated: ${data.created}\nUpdated: ${data.updated}`);
      } else {
        alert('⚠️ Sync completed with some issues. Check your Google Calendar.');
      }
    } catch (error) {
      console.error('Error syncing to Google:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to sync events to Google Calendar';
      alert(`❌ ${errorMsg}\n\nPlease try reconnecting your Google Calendar.`);
      
      // If auth error, reset connection status
      if (error.response?.status === 401 || error.response?.status === 403) {
        setGoogleSyncEnabled(false);
      }
    } finally {
      setSyncingToGoogle(false);
    }
  };

  // Disconnect Google Calendar
  const disconnectGoogleCalendar = async () => {
    if (!confirm('Are you sure you want to disconnect Google Calendar? Existing synced events will remain in your Google Calendar.')) {
      return;
    }
    
    try {
      await api.delete('/api/calendar/google/disconnect');
      setGoogleSyncEnabled(false);
      alert('✅ Google Calendar disconnected successfully');
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      alert('❌ Failed to disconnect Google Calendar');
    }
  };

  const loadRSVPCounts = async (eventsToCheck) => {
    try {
      const eventsNeedingRSVP = eventsToCheck.filter(event => event.requires_rsvp);
      
      if (eventsNeedingRSVP.length === 0) {
        return;
      }
      
      console.log(`Loading RSVP counts for ${eventsNeedingRSVP.length} events`);
      
      const rsvpPromises = eventsNeedingRSVP.map(async (event) => {
        const eventId = event.id || event._id;
        try {
          // Get detailed RSVP list to calculate total attendees including guests
          const response = await api.get(`/api/calendar/events/${eventId}/rsvps`);
          if (response.data?.rsvps) {
            const rsvps = response.data.rsvps;
            
            // Calculate counts including guests (same logic as export)
            const goingRsvps = rsvps.filter(rsvp => rsvp.rsvp_status === 'going');
            const maybeRsvps = rsvps.filter(rsvp => rsvp.rsvp_status === 'maybe');
            const notGoingRsvps = rsvps.filter(rsvp => rsvp.rsvp_status === 'not_going');
            
            // Calculate total attendees including guests (same as export totalAttendees calculation)
            const goingTotal = goingRsvps.reduce((sum, rsvp) => sum + 1 + (rsvp.guest_count || 0), 0);
            const maybeTotal = maybeRsvps.reduce((sum, rsvp) => sum + 1 + (rsvp.guest_count || 0), 0);
            
            console.log(`Event ${eventId}: ${goingTotal} going (including guests), ${maybeTotal} maybe`);
            
            return {
              eventId,
              rsvpCount: {
                going: goingTotal,
                maybe: maybeTotal, 
                not_going: notGoingRsvps.length,
                no_response: 0
              }
            };
          } else if (response.data) {
            // Fallback: if no rsvps array, try to get basic count data
            console.log(`Event ${eventId}: Using fallback rsvp_count data`);
            return {
              eventId,
              rsvpCount: response.data.rsvp_count || { going: 0, maybe: 0, not_going: 0, no_response: 0 }
            };
          }
        } catch (error) {
          console.error(`Error loading RSVP count for event ${eventId}:`, error);
        }
        return null;
      });
      
      const results = await Promise.all(rsvpPromises);
      const newRsvpCounts = { ...rsvpCounts };
      
      results.forEach(result => {
        if (result) {
          console.log(`Setting RSVP count for event ${result.eventId}:`, result.rsvpCount);
          newRsvpCounts[result.eventId] = result.rsvpCount;
        }
      });
      
      console.log('Updated RSVP counts:', newRsvpCounts);
      setRsvpCounts(newRsvpCounts);
      
    } catch (error) {
      console.error('Error loading RSVP counts:', error);
    }
  };

  const loadEvents = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/calendar/events');
      const loadedEvents = response.data.events || [];
      setEvents(loadedEvents);
      
      // Load RSVP counts for events that require them
      if (loadedEvents.length > 0) {
        await loadRSVPCounts(loadedEvents);
      }
    } catch (error) {
      console.error('Error loading events:', error);
      setEvents([]);
    }
    setLoading(false);
  };

  // Email attendees function
  const emailAttendees = async (event, attendees, currentAgenda = null) => {
    console.log('📧 Email Attendees clicked! Current settings:', emailSettings);
    console.log('📧 Event data:', event);
    console.log('📧 Current agenda passed:', currentAgenda);
    console.log('📧 Event type:', event.event_type);
    console.log('📧 Event type (lowercase):', event.event_type ? event.event_type.toLowerCase() : 'undefined');
    
    if (!emailSettings.enable_event_emails) {
      alert('Event email functionality is disabled by administrator');
      return;
    }

    if (!attendees || attendees.length === 0) {
      alert('No attendees to email');
      return;
    }

    const goingAttendees = attendees.filter(rsvp => rsvp.rsvp_status === 'going');
    const emails = goingAttendees
      .filter(rsvp => rsvp.email)
      .map(rsvp => rsvp.email);

    if (emails.length === 0) {
      alert('No email addresses found for going attendees');
      return;
    }

    // Check if admin configured in-app composer
    if (emailSettings.use_in_app_composer) {
      console.log('📧 Using in-app composer modal');
      // Open in-app email composer modal
      setEmailComposerModal({
        isOpen: true,
        event: event,
        attendees: attendees
      });
      return;
    } else {
      console.log('📧 Using mailto fallback');
    }

    // Use current agenda data if provided, otherwise fetch from API
    let agendaContent = '';
    const isMeetingType = event.event_type && ['board_meeting', 'arc_meeting', 'emergency_meeting'].includes(event.event_type.toLowerCase());
    console.log('📧 Is meeting type:', isMeetingType);
    
    if (isMeetingType) {
      let agendaItemsToUse = currentAgenda;
      console.log('📧 Agenda items to use (initial):', agendaItemsToUse);
      
      // If no current agenda provided, fetch from API as fallback
      if (!agendaItemsToUse || agendaItemsToUse.length === 0) {
        try {
          console.log('📋 Fetching agenda from API as fallback...');
          const response = await api.get(`/api/calendar/events/${event.id}/agenda`);
          console.log('📋 API response:', response.data);
          if (response.data.success && response.data.agenda && response.data.agenda.items && response.data.agenda.items.length > 0) {
            agendaItemsToUse = response.data.agenda.items;
            console.log('📋 Using agenda from API:', agendaItemsToUse);
          }
        } catch (error) {
          console.error('Error fetching agenda:', error);
          // Continue without agenda if fetch fails
        }
      } else {
        console.log('📋 Using current agenda data from event popup:', agendaItemsToUse);
      }
      
      // Format agenda content if we have items
      if (agendaItemsToUse && agendaItemsToUse.length > 0) {
        agendaContent = `\n\nMEETING AGENDA:
${agendaItemsToUse.map((item, index) => {
  let agendaItem = `${item.order || index + 1}. ${item.title}`;
  if (item.notes) {
    agendaItem += `\n   Notes: ${item.notes}`;
  }
  return agendaItem;
}).join('\n')}`;
        console.log('📋 Generated agenda content:', agendaContent);
      } else {
        console.log('📋 No agenda items found, agenda content will be empty');
      }
    } else {
      console.log('📧 Not a meeting type, skipping agenda');
    }

    // Fall back to mailto (legacy behavior)
    // Calculate total attendee count
    const totalGuestCount = goingAttendees.reduce((sum, rsvp) => sum + (rsvp.guest_count || 0), 0);
    const totalAttendees = goingAttendees.length + totalGuestCount;

    const subject = `EVENT INFO: ${event.title}`;
    // Use backend-formatted time if available, otherwise use start_local for HOA timezone
    const eventDate = event.start_datetime_display ? 
      event.start_datetime_display.split(' at ')[0] : 
      new Date(event.start_local || event.start_datetime).toLocaleDateString();
    const eventTime = event.all_day ? 'All Day' : 
      (event.start_datetime_display && event.end_datetime_display) ?
        `${event.start_datetime_display.split(' at ')[1]} - ${event.end_datetime_display}` :
        `${new Date(event.start_local || event.start_datetime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${new Date(event.end_local || event.end_datetime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
    
    let attendeeList = goingAttendees.map(rsvp => {
      const name = rsvp.user_name || `${rsvp.first_name || ''} ${rsvp.last_name || ''}`.trim() || 'Attendee';
      const guestCount = rsvp.guest_count || 0;
      return guestCount > 0 ? `${name} (+${guestCount} guest${guestCount > 1 ? 's' : ''})` : name;
    }).join('\n• ');

    let paymentInfo = '';
    if (event.is_paid && event.price) {
      paymentInfo = `\n\nPAYMENT INFORMATION:
This is a paid event: $${event.price} per person
Please ensure your payment is processed before the event.
Check the Payments section in your account for details.`;
    }

    const body = `Dear Event Attendees,

Below is your information for the upcoming event:

EVENT: ${event.title}
DATE: ${eventDate}
TIME: ${eventTime}
${event.location ? `LOCATION: ${event.location}` : ''}

${event.description ? `DETAILS: ${event.description}` : ''}${agendaContent}

CONFIRMED ATTENDEES (${totalAttendees} total):
• ${attendeeList}${paymentInfo}

We look forward to seeing you there!

Best regards,
${event.organizer || 'Event Organizer'}

---
Pro Tip: This email was sent using BCC to protect everyone's privacy. Please reply only to the organizer.`;

    console.log('📧 Final email body:', body);

    // Create mailto link with BCC
    const mailtoLink = `mailto:?bcc=${emails.join(';')}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open email client
    window.location.href = mailtoLink;
  };

  // Export attendees list
  const exportAttendees = async (event, attendees) => {
    if (!attendees || attendees.length === 0) {
      alert('No attendees to export');
      return;
    }

    // CRITICAL FIX: Use backend export endpoint which checks the ledger system
    try {
      const exportResponse = await api.get(`/api/calendar/events/${event.id}/export?format=json`);
      
      if (!exportResponse.data || !exportResponse.data.data) {
        throw new Error('No export data received from backend');
      }
      
      const exportData = exportResponse.data.data;
      const columns = exportResponse.data.columns || [];
      
      // Create CSV content from backend data
      const csvHeaders = columns.map(col => {
        // Convert snake_case to Title Case
        return col.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      });
      
      const csvRows = exportData.map(row => {
        return columns.map(col => {
          const value = row[col];
          // Handle various field types
          if (value === null || value === undefined || value === 'N/A') {
            return 'N/A';
          }
          // Convert dates to readable format
          if (col === 'submitted_at' && typeof value === 'object') {
            return new Date(value).toLocaleString();
          }
          return String(value).replace(/"/g, '""'); // Escape quotes
        });
      });

      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.map(field => `"${field}"`).join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${event.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_attendees.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert(`Exported ${exportData.length} attendee records to CSV file`);
      
    } catch (error) {
      console.error('Error exporting attendees:', error);
      alert('Failed to export attendees. Please try again or contact support.');
    }
  };

  const loadMonthlyHeaders = async () => {
    try {
      const year = currentDate.getFullYear();
      const response = await api.get(`/api/platform-admin/marketing/monthly-headers/${year}`);
      setMonthlyHeaders(response.data.headers || {});
    } catch (error) {
      console.error('Failed to load monthly headers:', error);
      // Use defaults if API fails
      setMonthlyHeaders({});
    }
  };

  useEffect(() => {
    loadEvents();
    loadMonthlyHeaders();
  }, []);

  useEffect(() => {
    loadMonthlyHeaders();
  }, [currentDate]);



  // Close month dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(event.target)) {
        setShowMonthDropdown(false);
      }
    };

    const handleMoreLinkClick = (event) => {
      // Check if the clicked element is a more link
      if (event.target.classList.contains('fc-more-link') || 
          event.target.closest('.fc-more-link')) {
        
        event.preventDefault();
        event.stopPropagation();
        
        console.log('Custom more link clicked');
        
        // Find the parent day cell
        const dayCell = event.target.closest('.fc-daygrid-day');
        if (dayCell) {
          const dateStr = dayCell.getAttribute('data-date');
          console.log('Day cell date:', dateStr);
          
          if (dateStr) {
            const clickedDate = new Date(dateStr + 'T00:00:00');
            console.log('Parsed date:', clickedDate);
            
            // Filter events for this day - use start_local (HOA timezone) for accurate day matching
            const dayEvents = events.filter(event => {
              // Use start_local if available, otherwise fall back to start_datetime
              const eventDateStr = event.start_local || event.start_datetime;
              const eventDate = new Date(eventDateStr);
              return eventDate.getFullYear() === clickedDate.getFullYear() &&
                     eventDate.getMonth() === clickedDate.getMonth() &&
                     eventDate.getDate() === clickedDate.getDate();
            });
            
            console.log(`Found ${dayEvents.length} events for ${dateStr}`);
            
            // Show our custom modal
            setDayEventsModal({
              date: clickedDate,
              events: dayEvents,
              show: true
            });
          }
        }
      }
    };

    if (showMonthDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Add the more link click handler
    document.addEventListener('click', handleMoreLinkClick, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('click', handleMoreLinkClick, true);
    };
  }, [showMonthDropdown, events]);

  const upcoming = useMemo(() => {
    return [...events]
      .filter(event => new Date(event.start_local || event.start_datetime) >= new Date())
      .sort((a, b) => new Date(a.start_local || a.start_datetime) - new Date(b.start_local || b.start_datetime));
  }, [events]);

  const onRSVP = async (event, status, guestCount = 0) => {
    try {
      // Handle both event.id and event._id for better compatibility
      const eventId = event.id || event._id;
      if (!eventId) {
        console.error('Error: Event ID missing. Cannot RSVP.');
        return;
      }

      if (!event.requires_rsvp) {
        console.warn('This event does not require RSVP.');
        return;
      }

      const rsvpData = {
        rsvp_status: status, // Use status directly since we're now passing the correct values
        guest_count: parseInt(guestCount) || 0,
        comments: ''
      };
      
      console.log('Sending RSVP:', rsvpData, 'for event:', eventId);
      
      const response = await api.post(`/api/calendar/events/${eventId}/rsvp`, rsvpData);
      console.log('RSVP response:', response.data);

      // Check if payment was created for paid events
      if (response.data.payment_required && response.data.payment_amount) {
        const totalAttendees = response.data.total_attendees || (1 + guestCount);
        const perPersonCost = response.data.per_person_cost || response.data.payment_amount;
        
        if (guestCount > 0) {
          alert(`RSVP confirmed! Payment for ${totalAttendees} attendees (you + ${guestCount} guest${guestCount > 1 ? 's' : ''}) totaling $${response.data.payment_amount} has been added to your payments. You can view and pay it in the Payments section.`);
        } else {
          alert(`RSVP confirmed! A payment of $${response.data.payment_amount} has been added to your payments. You can view and pay it in the Payments section.`);
        }
      }
      
      // Optimistically update the local event data immediately
      setEvents(prevEvents => 
        prevEvents.map(e => {
          if ((e.id || e._id) === eventId) {
            return {
              ...e,
              user_rsvp: {
                ...e.user_rsvp,
                rsvp_status: rsvpData.rsvp_status
              }
            };
          }
          return e;
        })
      );
      
      // No need to reload all events - optimistic update is sufficient
      
    } catch (error) {
      console.error('RSVP error:', error);
      console.error('RSVP error details:', error.response?.data);
      
      let errorMessage = 'Failed to update RSVP. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.status === 404) {
        errorMessage = 'Event not found. It may have been removed.';
      } else if (error.response?.status === 422) {
        errorMessage = 'Invalid RSVP data. Please try again.';
      }
      
      // Only show error alerts, not success alerts
      alert(errorMessage);
    }
  };

  const renderEventContent = (info) => (
    <div className="text-xs p-1 cursor-pointer hover:bg-black hover:bg-opacity-10 rounded transition-colors">
      <div className="font-medium truncate">{info.event.title}</div>
      <div className="opacity-70 truncate">{info.timeText}</div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-full">
      {/* Calendar (left) */}
      <div className="col-span-12 lg:col-span-7 bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Community Calendar</h2>
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Apple Calendar Button */}
            <a
              href={appleCalendarUrl}
              download="hoa-calendar.ics"
              className="px-4 py-2 rounded-xl bg-gray-800 text-white hover:bg-gray-900 transition-colors duration-200 whitespace-nowrap flex items-center gap-2 justify-center"
            >
              🍎 Apple Calendar
            </a>
            
            {/* Google Calendar Sync Buttons */}
            {/* GOOGLE CALENDAR SYNC COMMENTED OUT PER USER REQUEST
            {!checkingGoogleSync && (
              googleSyncEnabled ? (
                <div className="flex gap-2">
                  <button
                    onClick={syncToGoogleCalendar}
                    disabled={syncingToGoogle}
                    className="px-4 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700 transition-colors duration-200 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {syncingToGoogle ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Syncing...
                      </>
                    ) : (
                      <>
                        🔄 Sync to Google
                      </>
                    )}
                  </button>
                  <button
                    onClick={disconnectGoogleCalendar}
                    className="px-3 py-2 rounded-xl bg-gray-500 text-white hover:bg-gray-600 transition-colors duration-200 whitespace-nowrap"
                    title="Disconnect Google Calendar"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  onClick={connectGoogleCalendar}
                  className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-200 whitespace-nowrap flex items-center gap-2"
                >
                  📅 Connect Google Calendar
                </button>
              )
            )}
            */}
            
            {canCreateEvents && !permissionsLoading && (
              <button 
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition-colors duration-200 whitespace-nowrap"
                onClick={() => setShowForm(true)}
              >
                Add Event
              </button>
            )}
          </div>
        </div>
        
        {/* Monthly Header */}
        <div 
          className="relative h-32 rounded-xl mb-6 overflow-hidden"
          style={{ backgroundColor: getMonthColor(currentDate.getMonth()) }}
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${
                monthlyHeaders[currentDate.getMonth()]?.image || 
                getMonthHeader(currentDate.getMonth(), currentDate.getFullYear()).defaultImage
              })`,
              opacity: 0.7
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
          <div className="relative z-10 h-full flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-8 py-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1">
                {getMonthHeader(currentDate.getMonth(), currentDate.getFullYear()).name} {currentDate.getFullYear()}
              </h2>
              <p className="text-white/90 text-sm sm:text-lg">
                {monthlyHeaders[currentDate.getMonth()]?.theme || 
                 getMonthHeader(currentDate.getMonth(), currentDate.getFullYear()).theme}
              </p>
            </div>
            <div className="text-white/70 text-xs sm:text-sm mt-2 sm:mt-0">
              📅 Community Calendar
            </div>
          </div>
        </div>

        {/* Mobile List View for Phone Screens (<768px) */}
        {isPhoneScreen ? (
          <div className="mb-8">
            <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                📱 <strong>Mobile List View</strong> - Showing events in chronological order
              </p>
            </div>
            <MobileEventsList 
              events={events} 
              onEventClick={(event) => setActive(event)}
            />
          </div>
        ) : (
          <div 
            ref={calendarRef}
            className="calendar-wrapper calendar-container rounded-xl w-full"
            style={{
              border: `3px solid ${getMonthColor(currentDate.getMonth())}`,
              '--month-color': getMonthColor(currentDate.getMonth()),
              backgroundColor: 'white',
              padding: '1rem'
              /* REMOVED minHeight: '700px' - let FullCalendar control height */
            }}
          >
            <style>
              {`
              /* ============================
                 COMMUNITY CALENDAR LAYOUT
                 (iOS / Safari safe version)
                 ============================ */

              /* Main calendar wrapper card */
              .calendar-wrapper {
                width: 100% !important;
                max-width: 100% !important;
                box-sizing: border-box;
              }

              /* FullCalendar root */
              .calendar-container .fc {
                width: 100% !important;
                max-width: 100% !important;
                /* let FullCalendar control height */
              }

              /* Safari-safe height configuration */
              .calendar-container .fc-view-harness,
              .calendar-container .fc-daygrid-body,
              .calendar-container .fc-scrollgrid {
                height: auto !important;
                min-height: 520px !important;
                width: 100% !important;
              }

              .calendar-container .fc-scroller,
              .calendar-container .fc-scroller-harness {
                height: auto !important;
                max-height: none !important;
                overflow: visible !important;
              }

              /* ============================
                 CALENDAR STYLING
                 ============================ */

              .calendar-container .fc-daygrid-day {
                background: white !important;
              }

              .calendar-container .fc-scrollgrid {
                border: 1px solid #ddd !important;
              }

              .calendar-container .fc-event {
                cursor: pointer !important;
                border-radius: 4px !important;
                font-weight: 500 !important;
                min-height: 24px !important;
                padding: 2px 4px !important;
                margin: 1px !important;
                box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
                transition: all 0.2s ease !important;
              }

              .calendar-container .fc-event:hover {
                transform: translateY(-1px) !important;
                box-shadow: 0 2px 6px rgba(0,0,0,0.15) !important;
              }

              .calendar-container .fc-daygrid-event {
                white-space: normal !important;
              }

              .calendar-container .fc-day-today {
                background-color: ${getMonthColor(currentDate.getMonth())}15 !important;
              }

              .calendar-container .fc-more-link {
                color: ${getMonthColor(currentDate.getMonth())} !important;
                font-weight: 600 !important;
              }

              /* ============================
                 TOOLBAR / HEADER LAYOUT
                 ============================ */

              /* Base toolbar layout */
              .calendar-container .fc-header-toolbar {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                justify-content: space-between;
                gap: 0.5rem;
              }

              .calendar-container .fc-toolbar-chunk {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 0.5rem;
              }

              .calendar-container .fc-toolbar-title {
                color: ${getMonthColor(currentDate.getMonth())} !important;
                font-weight: 700 !important;
              }

              .calendar-container .fc-button-primary {
                background-color: ${getMonthColor(currentDate.getMonth())} !important;
                border-color: ${getMonthColor(currentDate.getMonth())} !important;
              }

              .calendar-container .fc-button-primary:hover {
                background-color: ${getMonthColor(currentDate.getMonth())}CC !important;
                border-color: ${getMonthColor(currentDate.getMonth())}CC !important;
              }

              /* ============================
                 MOBILE LAYOUT (phones)
                 ============================ */

              @media (max-width: 640px) {
                .calendar-container {
                  padding: 0.75rem !important;
                }

                /* Stack toolbar vertically on small screens */
                .calendar-container .fc-header-toolbar {
                  flex-direction: column;
                  align-items: stretch;
                  gap: 0.25rem;
                }

                .calendar-container .fc-toolbar-chunk {
                  width: 100%;
                  justify-content: space-between;
                }

                .calendar-container .fc-toolbar-title {
                  font-size: 1.1rem !important;
                  text-align: center;
                }
              }

              /* Extra compact for very narrow phones (older/smaller iPhones) */
              @media (max-width: 400px) {
                .calendar-container .fc-toolbar-title {
                  font-size: 1rem !important;
                }
              }
            `}
          </style>
          <FullCalendar
            ref={fcRef}
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            events={events.filter(e => {
              // Filter out events with invalid dates
              if (!e.start_datetime) {
                console.warn('Event missing start_datetime:', e);
                return false;
              }
              const startDate = new Date(e.start_datetime);
              if (isNaN(startDate.getTime())) {
                console.warn('Event has invalid start_datetime:', e.start_datetime, e);
                return false;
              }
              return true;
            }).map(e => {
              // CRITICAL: Use start_local (HOA timezone) for calendar display
              // Extract the date/time portion without timezone offset to prevent double conversion
              let startStr = e.start_local || e.start_datetime;
              let endStr = e.end_local || e.end_datetime || startStr;
              
              // If start_local has timezone offset (e.g., "2026-01-14T18:00:00-06:00"), 
              // strip it to prevent FullCalendar from converting to browser timezone
              if (startStr && startStr.includes('-06:00')) {
                startStr = startStr.replace('-06:00', '');
              } else if (startStr && startStr.includes('+')) {
                // Handle positive timezone offsets too
                startStr = startStr.split('+')[0];
              }
              if (endStr && endStr.includes('-06:00')) {
                endStr = endStr.replace('-06:00', '');
              } else if (endStr && endStr.includes('+')) {
                endStr = endStr.split('+')[0];
              }
              
              return {
                id: e.id || e._id || `event_${Date.now()}_${Math.random()}`,
                title: e.title || 'Untitled Event',
                start: startStr,
                end: endStr,
                allDay: !!e.all_day,
                backgroundColor: getEventTypeColor(e.event_type),
                borderColor: getEventTypeColor(e.event_type),
                textColor: '#ffffff',
                classNames: ['event-clickable']
              };
            })}
            eventClick={(arg) => {
              console.log('Event clicked:', arg.event.id);
              // Find event by multiple possible ID fields
              const eventId = arg.event.id;
              const event = events.find(x => 
                x.id === eventId || 
                x._id === eventId || 
                (x.id && x.id.toString() === eventId.toString()) ||
                (x._id && x._id.toString() === eventId.toString())
              );
              console.log('Found event:', event); 
              if (event) {
                setActive(event);
              } else {
                console.error('Event not found for ID:', eventId, 'Available events:', events.map(e => ({id: e.id, _id: e._id, title: e.title})));
              }
            }}
            eventContent={renderEventContent}
            expandRows={true}
            contentHeight={580}
            headerToolbar={{
              left: 'prev,next',
              center: 'title',
              right: 'monthDropdown,customToday'  
            }}
            dayMaxEvents={2}
            moreLinkClassNames="custom-more-link"
            moreLinkClick={false}
            eventDisplay="block"
            datesSet={(dateInfo) => {
              // Update current date when calendar view changes
              setCurrentDate(dateInfo.view.currentStart);
            }}
            customButtons={{
              customToday: {
                text: 'Today',
                click: function() {
                  const api = fcRef.current?.getApi();
                  if (api) {
                    api.today();
                    setCurrentDate(new Date());
                  }
                }
              },
              monthDropdown: {
                text: 'Month ▼',
                click: function() {
                  console.log('Month button clicked, toggling dropdown');
                  setShowMonthDropdown(!showMonthDropdown);
                }
              }
            }}
          />
          
          {/* Month Dropdown - positioned directly below Month button */}
          {showMonthDropdown && (
            <div 
              ref={monthDropdownRef}
              className="absolute z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-4"
              style={{
                top: '55px',
                right: '-5px',
                minWidth: '200px'
              }}
            >
              <h4 className="text-sm font-medium text-gray-900 mb-3">Select Month</h4>
              <div className="grid grid-cols-3 gap-2">
                {[
                  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
                ].map((month, index) => (
                  <button
                    key={month}
                    onClick={() => {
                      const api = fcRef.current?.getApi();
                      if (api) {
                        const targetDate = new Date(currentDate.getFullYear(), index, 1);
                        api.gotoDate(targetDate);
                        setCurrentDate(targetDate);
                        setShowMonthDropdown(false);
                      }
                    }}
                    className={`px-3 py-2 text-sm rounded-md transition-colors ${
                      currentDate.getMonth() === index
                        ? 'bg-indigo-100 text-indigo-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {month}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowMonthDropdown(false)}
                className="mt-3 w-full px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          )}
        </div>
        )}
        {/* End conditional: mobile list vs desktop calendar */}
      </div>
      {/* End col-span-12 lg:col-span-7 */}

      {/* Feed (right) */}
      <div className="col-span-12 lg:col-span-5 space-y-4 max-h-screen overflow-y-auto">
        <div className="bg-white rounded-2xl p-4 shadow-sm sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Upcoming Events</h3>
              <p className="text-sm text-gray-500">Stay connected with your community</p>
            </div>

          </div>
        </div>

        {loading && (
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <div className="animate-pulse text-center text-gray-500">Loading events...</div>
          </div>
        )}

        {!loading && upcoming.length === 0 && (
          <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
            <div className="text-gray-400 text-lg mb-2">📅</div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No upcoming events</h3>
            <p className="text-sm text-gray-500">Check back soon for community activities!</p>
          </div>
        )}

        {upcoming.map(event => (
          <EventCard 
            key={event.id} 
            event={event} 
            onRSVP={onRSVP}
            rsvpCount={rsvpCounts[event.id || event._id]}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setActive(event);
            }}
          />
        ))}
      </div>

      {showForm && (
        <EventFormModal 
          onClose={() => setShowForm(false)} 
          onSaved={() => {
            setShowForm(false);
            loadEvents();
          }}
        />
      )}
      
      {active && (
        <EventDrawer 
          event={active} 
          onClose={() => setActive(null)} 
          onRSVP={onRSVP}
          emailAttendees={emailAttendees}
          canCreateEvents={canCreateEvents}
        />
      )}

      {dayEventsModal.show && (
        <DayEventsModal 
          date={dayEventsModal.date}
          events={dayEventsModal.events}
          rsvpCounts={rsvpCounts}
          onClose={() => setDayEventsModal({ show: false, date: null, events: [] })}
          onEventClick={(event) => {
            setActive(event);
            setDayEventsModal({ show: false, date: null, events: [] });
          }}
          onRSVP={onRSVP}
        />
      )}

      <EmailComposerModal
        isOpen={emailComposerModal.isOpen}
        onClose={() => setEmailComposerModal({ isOpen: false, event: null, attendees: [] })}
        event={emailComposerModal.event}
        attendees={emailComposerModal.attendees}
        user={user}
      />
    </div>
  );
}

// Day Events Modal Component for showing all events on a specific day
function DayEventsModal({ date, events, rsvpCounts, onClose, onEventClick, onRSVP }) {
  // Add error handling for date formatting
  let formattedDate = 'Unknown Date';
  try {
    if (date && typeof date.toLocaleDateString === 'function') {
      formattedDate = date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } else if (date) {
      // Fallback if date is not a proper Date object
      const dateObj = new Date(date);
      if (!isNaN(dateObj.getTime())) {
        formattedDate = dateObj.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      }
    }
  } catch (error) {
    console.error('Error formatting date in DayEventsModal:', error);
    formattedDate = 'Date Error';
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-xl text-gray-900">Events for {formattedDate}</h3>
            <p className="text-sm text-gray-500">{events.length} {events.length === 1 ? 'event' : 'events'}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>
        
        <div className="p-6 space-y-4 max-h-[calc(90vh-100px)] overflow-y-auto">
          {events.map(event => {
            const eventImage = getEventCover(event.event_type, event.image_url);
            // CRITICAL: Use start_local/end_local for accurate HOA timezone display
            const startDate = new Date(event.start_local || event.start_datetime);
            const endDate = new Date(event.end_local || event.end_datetime);
            // Use backend-formatted times if available
            const displayStartTime = event.start_datetime_display ? 
              event.start_datetime_display.split(' at ')[1] || startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
              startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            const displayEndTime = event.end_datetime_display || endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            const eventId = event.id || event._id;
            const rsvpCount = rsvpCounts[eventId];
            const totalAttending = rsvpCount ? (rsvpCount.going || 0) + (rsvpCount.maybe || 0) : 0;
            const userRsvpStatus = event.user_rsvp?.rsvp_status || null;
            
            return (
              <div key={event.id || event._id} className="bg-gray-50 rounded-xl overflow-hidden hover:bg-gray-100 transition-colors">
                <div className="flex gap-4 p-4 cursor-pointer" onClick={() => onEventClick(event)}>
                  <img 
                    src={eventImage} 
                    alt={event.title}
                    className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                    onError={(e) => {
                      e.target.src = `/assets/events/stock/${STOCK_EVENT_COVERS.other}`;
                    }}
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs uppercase tracking-wide font-semibold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">
                        {event.event_type}
                      </span>
                      {event.requires_rsvp && (
                        <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                          RSVP Required
                        </span>
                      )}
                    </div>
                    
                    <h4 className="font-semibold text-lg text-gray-900 mb-1 truncate">{event.title}</h4>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                      <div className="flex items-center gap-1">
                        <span className="text-indigo-500">⏰</span>
                        <span>
                          {displayStartTime}
                          {displayEndTime && ` - ${displayEndTime}`}
                        </span>
                      </div>
                      
                      {event.location && (
                        <div className="flex items-center gap-1">
                          <span className="text-indigo-500">📍</span>
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                      
                      {event.requires_rsvp && totalAttending > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="text-green-500">👥</span>
                          <span>{totalAttending} attending</span>
                        </div>
                      )}
                    </div>
                    
                    {event.description && (
                      <p className="text-sm text-gray-700 line-clamp-2">{event.description}</p>
                    )}
                  </div>
                </div>
                
                {event.requires_rsvp && (
                  <div className="px-4 pb-4 border-t border-gray-200 pt-3">
                    <div className="flex gap-2">
                      <button 
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          userRsvpStatus === 'going'
                            ? 'bg-green-600 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRSVP(event, 'going');
                        }}
                      >
                        ✓ Going
                      </button>
                      <button 
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          userRsvpStatus === 'maybe'
                            ? 'bg-yellow-500 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRSVP(event, 'maybe');
                        }}
                      >
                        ? Maybe
                      </button>
                      <button 
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          userRsvpStatus === 'not_going'
                            ? 'bg-red-500 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRSVP(event, 'not_going');
                        }}
                      >
                        ✗ No
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          
          {events.length === 0 && (
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-2">📅</div>
              <p className="text-gray-600">No events found for this date.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Event Card Component
function EventCard({ event, onRSVP, onClick, rsvpCount }) {
  const eventImage = getEventCover(event.event_type, event.image_url);
  const tzName = event.tz?.tz_name || 'America/Chicago';
  const tzPretty = event.tz?.tz_pretty || 'Central Time';
  
  // USE THE LOCAL TIME THAT BACKEND ALREADY CONVERTED FOR US
  // Backend provides start_local and end_local which are already in HOA timezone
  const displayTime = event.start_local || event.start_datetime;
  
  // Format times - now using the already-converted local time
  const startTimeStr = formatTimeOnly(displayTime, tzName);
  const dateStr = formatInHOATimezone(displayTime, tzName, { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric',
    hour: undefined,
    minute: undefined
  });
  
  console.log('Event timezone debug:', {
    title: event.title,
    start_datetime_utc: event.start_datetime,
    start_local: event.start_local,
    using: displayTime,
    formatted: startTimeStr,
    tz: event.tz
  });
  
  // Calculate total attending from RSVP count
  const totalAttending = rsvpCount ? (rsvpCount.going || 0) + (rsvpCount.maybe || 0) : 0;
  
  // Get user's current RSVP status
  const userRsvpStatus = event.user_rsvp?.rsvp_status || null;
  
  return (
    <article className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer">
      <div onClick={onClick}>
        <img 
          src={eventImage} 
          alt={event.title}
          className="w-full h-44 object-cover"
          onError={(e) => {
            e.target.src = `/assets/events/stock/${STOCK_EVENT_COVERS.other}`;
          }}
        />
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide font-semibold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">
              {event.event_type}
            </span>
            {event.requires_rsvp && (
              <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                RSVP Required
              </span>
            )}
          </div>
          
          <h3 className="font-semibold text-lg text-gray-900 leading-tight">{event.title}</h3>
          
          <div className="space-y-1 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span className="text-indigo-500">📅</span>
              <span>{dateStr}</span>
              <span className="text-gray-400">•</span>
              <span className="font-medium">{startTimeStr}</span>
            </div>
            
            {event.location && (
              <div className="flex items-center gap-2">
                <span className="text-indigo-500">📍</span>
                <span>{event.location}</span>
              </div>
            )}
            
            {event.requires_rsvp && (
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-green-500">👥</span>
                  <span>
                    {rsvpCount?.going || 0} going
                    {(rsvpCount?.maybe || 0) > 0 && `, ${rsvpCount.maybe} maybe`}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          {event.description && (
            <p className="text-sm text-gray-700 line-clamp-2">{event.description}</p>
          )}
        </div>
      </div>
      
      {event.requires_rsvp && (
        <div className="px-4 pb-4">
          <div className="flex gap-2">
            <button 
              className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                userRsvpStatus === 'going' 
                  ? 'bg-green-600 text-white border-2 border-green-600' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onRSVP(event, 'going');
              }}
            >
              {userRsvpStatus === 'going' ? '✓ Going' : 'Going'}
            </button>
            <button 
              className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                userRsvpStatus === 'maybe' 
                  ? 'bg-yellow-500 text-white border-2 border-yellow-500' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onRSVP(event, 'maybe');
              }}
            >
              {userRsvpStatus === 'maybe' ? '✓ Maybe' : 'Maybe'}
            </button>
            <button 
              className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                userRsvpStatus === 'not_going' 
                  ? 'bg-red-500 text-white border-2 border-red-500' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onRSVP(event, 'not_going');
              }}
            >
              {userRsvpStatus === 'not_going' ? '✓ No' : 'No'}
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

// Event Type Colors
function getEventTypeColor(eventType) {
  const colors = {
    'Board Meeting': '#ef4444',
    'Maintenance': '#f59e0b',
    'ARC Meeting': '#8b5cf6',
    'Pool Party': '#06b6d4',
    'Community Event': '#3b82f6',
    'Holiday Event': '#10b981',
    'Emergency Meeting': '#dc2626',
    'Social Meeting': '#ec4899',
    'Workshop': '#6366f1',
    'Fundraiser': '#f97316',
    'Other': '#6b7280'
  };
  return colors[eventType] || colors['Other'];
}

// Monthly colors for visual appeal
function getMonthColor(monthIndex) {
  const monthColors = [
    '#3B82F6', // January - Royal Blue
    '#EC4899', // February - Pink 
    '#10B981', // March - Green
    '#F59E0B', // April - Amber
    '#8B5CF6', // May - Purple
    '#06B6D4', // June - Cyan
    '#EF4444', // July - Red
    '#F97316', // August - Orange
    '#6366F1', // September - Indigo
    '#84CC16', // October - Lime
    '#8B4513', // November - Brown
    '#059669', // December - Emerald
  ];
  return monthColors[monthIndex] || '#6B7280';
}

// Monthly header configurations
function getMonthHeader(monthIndex, year) {
  const monthHeaders = [
    { name: 'January', theme: 'New Beginnings', defaultImage: '/assets/calendar/headers/january.jpg' },
    { name: 'February', theme: 'Love & Community', defaultImage: '/assets/calendar/headers/february.jpg' },
    { name: 'March', theme: 'Spring Awakening', defaultImage: '/assets/calendar/headers/march.jpg' },
    { name: 'April', theme: 'Fresh Blooms', defaultImage: '/assets/calendar/headers/april.jpg' },
    { name: 'May', theme: 'Growing Together', defaultImage: '/assets/calendar/headers/may.jpg' },
    { name: 'June', theme: 'Summer Joy', defaultImage: '/assets/calendar/headers/june.jpg' },
    { name: 'July', theme: 'Independence & Unity', defaultImage: '/assets/calendar/headers/july.jpg' },
    { name: 'August', theme: 'Summer Memories', defaultImage: '/assets/calendar/headers/august.jpg' },
    { name: 'September', theme: 'Harvest Season', defaultImage: '/assets/calendar/headers/september.jpg' },
    { name: 'October', theme: 'Autumn Colors', defaultImage: '/assets/calendar/headers/october.jpg' },
    { name: 'November', theme: 'Gratitude & Togetherness', defaultImage: '/assets/calendar/headers/november.jpg' },
    { name: 'December', theme: 'Holiday Spirit', defaultImage: '/assets/calendar/headers/december.jpg' }
  ];
  return monthHeaders[monthIndex] || monthHeaders[0];
}

// Event Drawer Component
function EventDrawer({ event, onClose, onRSVP, emailAttendees, canCreateEvents }) {
  const { user } = useAuth();  // Use global auth context instead of local state
  const [rsvpDetails, setRsvpDetails] = useState(null);
  const [loadingRsvps, setLoadingRsvps] = useState(false);
  const [showEditEvent, setShowEditEvent] = useState(false);
  const [showAgendaModal, setShowAgendaModal] = useState(false);
  const [hasAgenda, setHasAgenda] = useState(false);
  const [agendaItems, setAgendaItems] = useState([]);
  const eventImage = getEventCover(event.event_type, event.image_url);
  
  // Use backend-provided formatted times (already in HOA timezone)
  // CRITICAL: Use start_local/end_local for accurate HOA timezone display
  // Fallback to start_datetime/end_datetime only if local versions not available
  const startDate = new Date(event.start_local || event.start_datetime);
  const endDate = new Date(event.end_local || event.end_datetime);
  const displayStartDate = event.start_datetime_display || startDate.toLocaleDateString();
  const displayStartTime = event.start_datetime_display ? 
    event.start_datetime_display.split(' at ')[1] || startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
    startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const displayEndTime = event.end_datetime_display || endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
  const userRsvpStatus = event.user_rsvp?.rsvp_status || null;

  // Check if user is board member - using global auth user
  const isBoard = user?.role && ['board_member', 'president', 'vice_president', 'treasurer', 'secretary', 'platform_admin'].includes(user.role.toLowerCase());
  
  // Local function to check if user can manage this event
  const canManageEvent = (event) => {
    // Board members can always manage any event
    if (isBoard) return true;
    
    // For non-board members, they need event creation permissions to manage events
    // AND they can only manage their own events if they created them
    if (canCreateEvents && event?.created_by === user?.id) return true;
    
    return false;
  };
  
  // Check if this is a meeting type
  const isMeetingType = event.event_type === 'board_meeting' || event.event_type === 'arc_meeting';

  // Fetch RSVP details when drawer opens - ALWAYS trigger for RSVP events
  useEffect(() => {
    console.log('EventDrawer mounted with event:', event.title, 'ID:', event.id || event._id, 'requires_rsvp:', event.requires_rsvp);
    
    if (event.requires_rsvp) {
      loadRsvpDetails();
    } else {
      console.log('Event does not require RSVP, skipping member tracking');
    }

  }, [event]); // Watch the entire event object
  
  // Check if agenda exists for meeting types
  useEffect(() => {
    if (isMeetingType && event.id) {
      checkAgenda();
    }
  }, [event.id, isMeetingType]);
  
  const checkAgenda = async () => {
    try {
      const response = await api.get(`/api/calendar/events/${event.id}/agenda`);
      if (response.data.success && response.data.agenda && response.data.agenda.items) {
        setHasAgenda(response.data.agenda.items.length > 0);
        setAgendaItems(response.data.agenda.items);
      } else {
        setHasAgenda(false);
        setAgendaItems([]);
      }
    } catch (error) {
      console.error('Error checking agenda:', error);
      setHasAgenda(false);
      setAgendaItems([]);
    }
  };

  const loadRsvpDetails = async () => {
    const eventId = event.id || event._id;
    if (!eventId) {
      console.error('No event ID found for RSVP loading');
      return;
    }
    
    console.log('🔍 LOADING RSVP DETAILS for event:', eventId);
    setLoadingRsvps(true);
    
    try {
      const response = await api.get(`/api/calendar/events/${eventId}/rsvps`);
      console.log('✅ RSVP details loaded:', response.data);
      setRsvpDetails(response.data.rsvps || response.data);
    } catch (error) {
      console.error('❌ Error loading RSVP details:', error);
      console.error('Error response:', error.response?.data);
      
      // Even on error, set empty array so UI still shows
      setRsvpDetails([]);
    } finally {
      setLoadingRsvps(false);
    }
  };

  // Duplicate emailAttendees function removed - using the one with settings check above

  // Export attendees list
  const exportAttendees = async (event, attendees) => {
    if (!attendees || attendees.length === 0) {
      alert('No attendees to export');
      return;
    }

    // CRITICAL FIX: Use backend export endpoint which checks the ledger system
    try {
      const exportResponse = await api.get(`/api/calendar/events/${event.id}/export?format=json`);
      
      if (!exportResponse.data || !exportResponse.data.data) {
        throw new Error('No export data received from backend');
      }
      
      const exportData = exportResponse.data.data;
      const columns = exportResponse.data.columns || [];
      
      // Create CSV content from backend data
      const csvHeaders = columns.map(col => {
        // Convert snake_case to Title Case
        return col.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      });
      
      const csvRows = exportData.map(row => {
        return columns.map(col => {
          const value = row[col];
          // Handle various field types
          if (value === null || value === undefined || value === 'N/A') {
            return 'N/A';
          }
          // Convert dates to readable format
          if (col === 'submitted_at' && typeof value === 'object') {
            return new Date(value).toLocaleString();
          }
          return String(value).replace(/"/g, '""'); // Escape quotes
        });
      });

      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.map(field => `"${field}"`).join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${event.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_attendees.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert(`Exported ${exportData.length} attendee records to CSV file`);
      
    } catch (error) {
      console.error('Error exporting attendees:', error);
      alert('Failed to export attendees. Please try again or contact support.');
    }
  };

  // Calculate totals
  const rsvpCounts = (rsvpDetails && Array.isArray(rsvpDetails)) ? {
    going: rsvpDetails.filter(r => r.rsvp_status === 'going').length,
    maybe: rsvpDetails.filter(r => r.rsvp_status === 'maybe').length,
    not_going: rsvpDetails.filter(r => r.rsvp_status === 'not_going').length
  } : { going: 0, maybe: 0, not_going: 0 };

  const totalResponses = rsvpCounts.going + rsvpCounts.maybe + rsvpCounts.not_going;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-md bg-white h-full overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between">
          <h3 className="font-semibold text-lg text-gray-900">Event Details</h3>
          <div className="flex items-center gap-2">
            {/* Edit button - show based on permission system */}
            {canManageEvent(event) && (
              <button 
                onClick={() => setShowEditEvent(true)}
                className="p-2 hover:bg-blue-100 rounded-lg transition-colors text-blue-600"
                title="Edit Event"
              >
                ✏️
              </button>
            )}
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
        
        <div className="p-5 space-y-4">
          <img 
            src={eventImage} 
            className="w-full rounded-xl" 
            alt="event"
            onError={(e) => {
              e.target.src = `/assets/events/stock/${STOCK_EVENT_COVERS.other}`;
            }}
          />
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide font-semibold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">
                {event.event_type}
              </span>
              {event.requires_rsvp && (
                <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                  RSVP Required
                </span>
              )}
            </div>
            
            <h3 className="font-semibold text-xl text-gray-900">{event.title}</h3>
            
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span className="text-indigo-500">📅</span>
                <span>{displayStartDate.split(' at ')[0] || displayStartDate}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-indigo-500">⏰</span>
                <span>
                  {displayStartTime} - {displayEndTime}
                </span>
              </div>
              {event.location && (
                <div className="flex items-center gap-2">
                  <span className="text-indigo-500">📍</span>
                  <span>{event.location}</span>
                </div>
              )}
            </div>
            
            {event.description && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                <p className="text-sm text-gray-700 leading-relaxed">{event.description}</p>
              </div>
            )}
            
            {/* Agenda Display for BOD/ARC Meetings */}
            {isMeetingType && hasAgenda && agendaItems.length > 0 && (
              <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <h4 className="font-medium text-purple-900 mb-3 flex items-center gap-2">
                  <span>📋</span> Meeting Agenda
                </h4>
                <ol className="space-y-2 text-sm text-gray-700">
                  {agendaItems.map((item, index) => (
                    <li key={item.id || index} className="flex gap-2">
                      <span className="font-medium text-purple-700">{item.order}.</span>
                      <div className="flex-1">
                        <span>{item.title}</span>
                        {item.notes && (
                          <p className="text-xs text-gray-600 mt-1 ml-4">{item.notes}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            
            {/* Price Information for Paid Events */}
            {event.is_paid && (
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center gap-2">
                  <span className="text-yellow-600">💰</span>
                  <h4 className="font-medium text-yellow-900">Paid Event</h4>
                </div>
                <div className="mt-1">
                  <span className="text-lg font-bold text-yellow-900">
                    ${event.price ? parseFloat(event.price).toFixed(2) : 'Price TBD'}
                  </span>
                  <span className="text-sm text-yellow-700 ml-2">per person</span>
                </div>
                {event.payment_method && (
                  <p className="text-xs text-yellow-700 mt-1">
                    Payment method: {event.payment_method}
                  </p>
                )}
              </div>
            )}
            
            {event.requires_rsvp && (
              <div className="mt-6 pt-4 border-t">
                <h4 className="font-medium text-gray-900 mb-3">RSVP to this event</h4>
                <RSVPInterface event={event} userRsvpStatus={userRsvpStatus} onRSVP={onRSVP} />
                
                {/* RSVP Tracking Section */}
                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">RSVP Responses</h4>
                    {loadingRsvps && <span className="text-sm text-gray-500">Loading...</span>}
                  </div>
                  
                  {/* RSVP Summary */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{rsvpCounts.going}</div>
                      <div className="text-sm text-green-700">Going</div>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">{rsvpCounts.maybe}</div>
                      <div className="text-sm text-yellow-700">Maybe</div>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{rsvpCounts.not_going}</div>
                      <div className="text-sm text-red-700">Not Going</div>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-600 mb-4">
                    Total responses: {totalResponses}
                  </div>
                  
                  {/* RSVP Member Lists */}
                  {rsvpDetails && Array.isArray(rsvpDetails) && rsvpDetails.length > 0 && (
                    <div className="space-y-4">
                      {/* Going List */}
                      {rsvpCounts.going > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-green-700 mb-2">
                            Going ({rsvpCounts.going})
                          </h5>
                          <div className="space-y-1">
                            {(rsvpDetails && Array.isArray(rsvpDetails) ? rsvpDetails : [])
                              .filter(r => r.rsvp_status === 'going')
                              .map((rsvp, index) => (
                                <div key={index} className="flex items-center gap-2 text-sm">
                                  <span className="text-green-500">✓</span>
                                  <span>{rsvp.user_name || (rsvp.first_name && rsvp.last_name ? `${rsvp.first_name} ${rsvp.last_name}` : null) || rsvp.email || 'Unknown User'}</span>
                                  {rsvp.guest_count > 0 && (
                                    <span className="text-gray-500">+{rsvp.guest_count} guest{rsvp.guest_count > 1 ? 's' : ''}</span>
                                  )}
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Maybe List */}
                      {rsvpCounts.maybe > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-yellow-700 mb-2">
                            Maybe ({rsvpCounts.maybe})
                          </h5>
                          <div className="space-y-1">
                            {(rsvpDetails && Array.isArray(rsvpDetails) ? rsvpDetails : [])
                              .filter(r => r.rsvp_status === 'maybe')
                              .map((rsvp, index) => (
                                <div key={index} className="flex items-center gap-2 text-sm">
                                  <span className="text-yellow-500">?</span>
                                  <span>{rsvp.user_name || (rsvp.first_name && rsvp.last_name ? `${rsvp.first_name} ${rsvp.last_name}` : null) || rsvp.email || 'Unknown User'}</span>
                                  {rsvp.guest_count > 0 && (
                                    <span className="text-gray-500">+{rsvp.guest_count} guest{rsvp.guest_count > 1 ? 's' : ''}</span>
                                  )}
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Not Going List */}
                      {rsvpCounts.not_going > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-red-700 mb-2">
                            Not Going ({rsvpCounts.not_going})
                          </h5>
                          <div className="space-y-1">
                            {(rsvpDetails && Array.isArray(rsvpDetails) ? rsvpDetails : [])
                              .filter(r => r.rsvp_status === 'not_going')
                              .map((rsvp, index) => (
                                <div key={index} className="flex items-center gap-2 text-sm">
                                  <span className="text-red-500">✗</span>
                                  <span>{rsvp.user_name || (rsvp.first_name && rsvp.last_name ? `${rsvp.first_name} ${rsvp.last_name}` : null) || rsvp.email || 'Unknown User'}</span>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {rsvpDetails && Array.isArray(rsvpDetails) && rsvpDetails.length === 0 && (
                    <div className="text-center py-4 text-gray-500">
                      <div className="text-4xl mb-2">📝</div>
                      <p>No RSVPs yet</p>
                    </div>
                  )}

                  {/* Event Actions - Show for organizers and board members only */}
                  {/* DEBUG: Always show for troubleshooting */}
                  {console.log('🔍 EVENT ACTIONS DEBUG:', {
                    canManageEvent: canManageEvent(event),
                    user_id: user?.id,
                    event_created_by: event.created_by,
                    isBoard,
                    user_role: user?.role
                  })}
                  {canManageEvent(event) && (
                    <div className="bg-blue-50 rounded-lg p-4 mt-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-900">
                          Event Actions: {isBoard ? '(Board Member)' : ''} {event.created_by === user?.id ? '(Organizer)' : ''}
                        </span>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => emailAttendees(event, rsvpDetails || [], agendaItems)}
                            disabled={!rsvpDetails || !Array.isArray(rsvpDetails) || rsvpDetails.length === 0}
                            className={`inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                              rsvpDetails && Array.isArray(rsvpDetails) && rsvpDetails.length > 0 ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'
                            }`}
                          >
                            📧 Email Attendees
                          </button>
                          <button
                            onClick={() => exportAttendees(event, rsvpDetails || [])}
                            disabled={!rsvpDetails || !Array.isArray(rsvpDetails) || rsvpDetails.length === 0}
                            className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                              rsvpDetails && Array.isArray(rsvpDetails) && rsvpDetails.length > 0 ? 'text-gray-700 bg-white hover:bg-gray-50' : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                            }`}
                          >
                            📊 Export List
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-blue-700 mt-2">
                        💡 Pro Tip: Email attendees using BCC to protect everyone&apos;s privacy. Only organizers and board members can access these actions.
                      </p>
                    </div>
                  )}
                  
                  {/* Agenda Management for BOD/ARC Meetings - Board Only */}
                  {isMeetingType && isBoard && (
                    <div className="bg-purple-50 rounded-lg p-4 mt-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-purple-900">
                          📋 Meeting Agenda
                        </span>
                      </div>
                      
                      <div className="flex gap-2 mt-3">
                        {hasAgenda ? (
                          <>
                            <button
                              onClick={() => setShowAgendaModal(true)}
                              className="inline-flex items-center px-3 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700"
                            >
                              ✏️ Edit Agenda
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await api.post(`/api/calendar/events/${event.id}/agenda/email`);
                                  alert('Agenda emailed to all homeowners!');
                                } catch (error) {
                                  console.error('Error emailing agenda:', error);
                                  alert('Failed to email agenda');
                                }
                              }}
                              className="inline-flex items-center px-3 py-2 border border-purple-300 text-sm font-medium rounded-md text-purple-700 bg-white hover:bg-purple-50"
                            >
                              📧 Email Agenda
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setShowAgendaModal(true)}
                            className="inline-flex items-center px-3 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700"
                          >
                            ➕ Add Agenda
                          </button>
                        )}
                      </div>
                      
                      <p className="text-xs text-purple-700 mt-2">
                        💡 Add a meeting agenda to keep everyone informed and organized.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Event Modal */}
      {showEditEvent && (
        <EditEventModal
          event={event}
          onClose={() => setShowEditEvent(false)}
          onSaved={() => {
            setShowEditEvent(false);
            onClose(); // Close the drawer and refresh events
          }}
        />
      )}
      
      {/* Agenda Modal */}
      {showAgendaModal && (
        <AgendaModal
          event={event}
          onClose={() => {
            setShowAgendaModal(false);
            checkAgenda(); // Refresh agenda status
          }}
          onSaved={() => {
            checkAgenda(); // Refresh agenda status
          }}
        />
      )}
    </div>
  );
}

// Edit Event Modal Component
function EditEventModal({ event, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [showAgendaModal, setShowAgendaModal] = useState(false);
  const [formData, setFormData] = useState({
    title: event.title || '',
    event_type: event.event_type || 'community_event',
    visibility: event.visibility || 'public',
    // Use start_local for edit form to show correct HOA timezone time
    start_datetime: event.start_local ? new Date(event.start_local).toISOString().slice(0, 16) : 
                    (event.start_datetime ? new Date(event.start_datetime).toISOString().slice(0, 16) : ''),
    end_datetime: event.end_local ? new Date(event.end_local).toISOString().slice(0, 16) : 
                  (event.end_datetime ? new Date(event.end_datetime).toISOString().slice(0, 16) : ''),
    all_day: event.all_day || false,
    location: event.location || '',
    description: event.description || '',
    requires_rsvp: event.requires_rsvp !== undefined ? event.requires_rsvp : true,
    max_attendees: event.max_attendees || null,
    guest_allowed: event.guest_allowed || false,
    is_paid: event.is_paid || false,
    price: event.price || null,
    // RSVP deadline fields
    rsvp_deadline_type: event.rsvp_deadline_type || 'until_event',
    rsvp_custom_days: event.rsvp_custom_days ?? null,
    rsvp_deadline: event.rsvp_deadline ? new Date(event.rsvp_deadline).toISOString().slice(0, 16) : null,
    // Recurring event fields
    is_recurring: event.is_recurring || false,
    recurrence_type: event.recurrence_type || 'none',
    recurrence_interval: event.recurrence_interval || 1,
    recurrence_end_date: event.recurrence_end_date ? new Date(event.recurrence_end_date).toISOString().slice(0, 10) : null,
    recurrence_pattern: event.recurrence_pattern || null,
    recurrence_days: event.recurrence_days || [],
    recurrence_week_of_month: event.recurrence_week_of_month || null,
    recurrence_day_of_week: event.recurrence_day_of_week || null
  });
  
  const isMeetingType = formData.event_type === 'board_meeting' || formData.event_type === 'arc_meeting';

  // If Paid is turned on, automatically require RSVP
  useEffect(() => {
    if (formData.is_paid && !formData.requires_rsvp) {
      setFormData(prev => ({ ...prev, requires_rsvp: true }));
    }
  }, [formData.is_paid]);

  const handleDeleteEvent = async () => {
    try {
      // Check if event is paid and has payments
      if (formData.is_paid) {
        // TODO: Check if payments have been processed
        // For now, show warning
        const confirmDelete = confirm(
          "⚠️ WARNING: This is a paid event. If payments have already been processed, you must contact support to delete this event.\n\nIf no payments have been processed yet, click OK to delete."
        );
        if (!confirmDelete) return;
      } else {
        const confirmDelete = confirm("Are you sure you want to delete this event?");
        if (!confirmDelete) return;
      }
      
      const eventId = event.id || event._id;
      await api.delete(`/api/calendar/events/${eventId}`);
      alert('Event deleted successfully');
      onSaved();
    } catch (error) {
      console.error('Error deleting event:', error);
      if (error.response?.data?.detail) {
        alert(`Failed to delete event: ${error.response.data.detail}`);
      } else {
        alert('Failed to delete event. Please try again or contact support.');
      }
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Build event data, excluding null/empty optional fields
      const eventData = {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        start_datetime: new Date(formData.start_datetime).toISOString(),
        end_datetime: new Date(formData.end_datetime).toISOString(),
        all_day: formData.all_day,
        event_type: formData.event_type,
        visibility: formData.visibility,
        requires_rsvp: formData.requires_rsvp,
        is_paid: formData.is_paid,
        rsvp_deadline_type: formData.rsvp_deadline_type,
      };
      
      // Only add optional fields if they have values
      if (formData.max_attendees) {
        eventData.max_attendees = parseInt(formData.max_attendees, 10);
      }
      if (formData.is_paid && formData.price) {
        eventData.price = parseFloat(formData.price);
      }
      if (formData.rsvp_deadline_type === 'custom' && formData.rsvp_custom_days) {
        eventData.rsvp_custom_days = parseInt(formData.rsvp_custom_days, 10);
      }
      if (formData.rsvp_deadline) {
        eventData.rsvp_deadline = new Date(formData.rsvp_deadline).toISOString();
      }
      if (formData.recurrence_pattern) {
        eventData.recurrence_pattern = formData.recurrence_pattern;
      }
      if (formData.recurrence_end_date) {
        eventData.recurrence_end_date = new Date(formData.recurrence_end_date).toISOString();
      }

      const eventId = event.id || event._id;
      await api.put(`/api/calendar/events/${eventId}`, eventData);
      
      alert('Event updated successfully!');
      onSaved();
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Failed to update event. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div 
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Edit Event</h2>
            <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }} 
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter event title"
            />
          </div>

          {/* Event Type - DISABLED, cannot change type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Type (Cannot be changed)</label>
            <select
              value={formData.event_type}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
            >
              <option value="board_meeting">Board Meeting</option>
              <option value="arc_meeting">ARC Meeting</option>
              <option value="community_event">Community Event</option>
              <option value="maintenance">Maintenance</option>
              <option value="social_meeting">Social Meeting</option>
              <option value="pool_party">Pool Party</option>
              <option value="workshop">Workshop</option>
              <option value="holiday_event">Holiday Event</option>
              <option value="fundraiser">Fundraiser</option>
              <option value="emergency_meeting">Emergency Meeting</option>
              <option value="other">Other</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Delete and create new event to change type</p>
          </div>

          {/* All Day Event Checkbox - place BEFORE date fields */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.all_day}
              onChange={(e) => {
                e.stopPropagation();
                setFormData(prev => ({...prev, all_day: e.target.checked}));
              }}
              className="text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm font-medium text-gray-700">All Day Event</span>
          </label>

          {/* Date and Time - conditionally show time based on all_day */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {formData.all_day ? 'Start Date' : 'Start Date & Time'}
              </label>
              <input
                type={formData.all_day ? 'date' : 'datetime-local'}
                value={formData.all_day ? formData.start_datetime.split('T')[0] : formData.start_datetime}
                onChange={(e) => {
                  const value = formData.all_day ? `${e.target.value}T00:00` : e.target.value;
                  setFormData(prev => ({...prev, start_datetime: value}));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {formData.all_day ? 'End Date' : 'End Date & Time'}
              </label>
              <input
                type={formData.all_day ? 'date' : 'datetime-local'}
                value={formData.all_day ? formData.end_datetime.split('T')[0] : formData.end_datetime}
                onChange={(e) => {
                  const value = formData.all_day ? `${e.target.value}T23:59` : e.target.value;
                  setFormData(prev => ({...prev, end_datetime: value}));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData(prev => ({...prev, location: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Event location"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              rows="3"
              placeholder="Event description"
            />
          </div>

          {/* Event Options */}
          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.requires_rsvp}
                onChange={(e) => setFormData(prev => ({...prev, requires_rsvp: e.target.checked}))}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-gray-700">Require RSVP</span>
            </label>

            {(formData.requires_rsvp || formData.is_paid) && (
              <div className="mt-3 space-y-2">
                <label className="block text-sm font-medium text-gray-700">RSVP Deadline</label>
                <div className="grid md:grid-cols-2 gap-3">
                  <select
                    className="border rounded px-3 py-2"
                    value={formData.rsvp_deadline_type}
                    onChange={e => setFormData(prev => ({ ...prev, rsvp_deadline_type: e.target.value }))}
                  >
                    <option value="until_event">Until event start</option>
                    <option value="1_day_before">1 day before</option>
                    <option value="2_days_before">2 days before</option>
                    <option value="1_week_before">1 week before</option>
                    <option value="custom">Custom date/time</option>
                  </select>

                  {formData.rsvp_deadline_type === 'custom' ? (
                    <input
                      type="datetime-local"
                      className="border rounded px-3 py-2"
                      value={formData.rsvp_deadline}
                      onChange={e => setFormData(prev => ({ ...prev, rsvp_deadline: e.target.value }))}
                    />
                  ) : (
                    <input
                      type="number"
                      min="1"
                      placeholder="Custom days before (optional)"
                      className="border rounded px-3 py-2"
                      value={formData.rsvp_custom_days ?? ''}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        rsvp_custom_days: e.target.value ? parseInt(e.target.value, 10) : null
                      }))}
                    />
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  If Paid is selected, RSVP is automatically required so you can manage a guest list and payments.
                </p>
              </div>
            )}

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.guest_allowed}
                onChange={(e) => setFormData(prev => ({...prev, guest_allowed: e.target.checked}))}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-gray-700">Allow guests</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_paid}
                onChange={(e) => setFormData(prev => ({...prev, is_paid: e.target.checked}))}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-gray-700">Paid event</span>
            </label>

            {formData.is_paid && (
              <div className="ml-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Cost ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({...prev, price: parseFloat(e.target.value) || 0}))}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0.00"
                />
              </div>
            )}
          </div>

          {/* Max Attendees */}
          {formData.requires_rsvp && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Attendees (Optional)</label>
              <input
                type="number"
                value={formData.max_attendees}
                onChange={(e) => setFormData(prev => ({...prev, max_attendees: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Leave empty for unlimited"
              />
            </div>
          )}

          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
            <select
              value={formData.visibility}
              onChange={(e) => setFormData(prev => ({...prev, visibility: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="public">All Homeowners</option>
              <option value="board_only">Board Members Only</option>
              <option value="private">Private</option>
            </select>
          </div>
          
          {/* Agenda Management for BOD/ARC Meetings */}
          {isMeetingType && (
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-purple-900">📋 Meeting Agenda</span>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowAgendaModal(true);
                }}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Manage Agenda
              </button>
              <p className="text-xs text-purple-700 mt-2">
                💡 Add or edit the meeting agenda. Homeowners will be able to view it.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-white border-t p-6">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDeleteEvent();
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Delete Event
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit();
              }}
              disabled={saving || !formData.title}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors"
            >
              {saving ? 'Updating...' : 'Update Event'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Agenda Modal */}
      {showAgendaModal && (
        <AgendaModal
          event={event}
          onClose={() => {
            setShowAgendaModal(false);
          }}
          onSaved={() => {
            // Agenda saved
          }}
        />
      )}
    </div>
  );
}

// Event Form Modal Component
function EventFormModal({ onClose, onSaved }) {
  const { user } = useAuth();  // ← ADD THIS - needed for hoa_id and created_by
  const [file, setFile] = useState();
  const [saving, setSaving] = useState(false);
  const [useStockImage, setUseStockImage] = useState(true);
  const [agendaOption, setAgendaOption] = useState('no_agenda'); // no_agenda, add_now, add_later
  const [showAgendaModal, setShowAgendaModal] = useState(false);
  const [createdEvent, setCreatedEvent] = useState(null); // Store created event for agenda modal
  const [formData, setFormData] = useState({
    title: '',
    event_type: 'community_event', // Fixed: use enum value instead of display label
    visibility: 'public',
    start_datetime: new Date().toISOString().slice(0, 16),
    end_datetime: new Date(Date.now() + 3600000).toISOString().slice(0, 16),
    all_day: false,
    location: '',
    description: '',
    requires_rsvp: true,
    max_attendees: '',
    is_paid: false,
    price: '',
    // NEW — RSVP deadline fields
    rsvp_deadline_type: 'until_event', // 'until_event','1_day_before','2_days_before','1_week_before','custom'
    rsvp_custom_days: null,               // number of days before the event
    rsvp_deadline: '', // only used if type === 'custom'
    // Recurring event fields
    is_recurring: false,
    recurrence_type: 'none', // none, weekly, monthly, yearly
    recurrence_interval: 1,
    recurrence_end_date: '',
    recurrence_pattern: '', // e.g., "third_thursday", "every_tuesday"
    recurrence_days: [], // for weekly recurring
    recurrence_week_of_month: '', // first, second, third, fourth, last
    recurrence_day_of_week: '' // monday, tuesday, etc.
  });
  
  // Check if event type is BOD or ARC meeting
  const isMeetingType = formData.event_type === 'board_meeting' || formData.event_type === 'arc_meeting';

  // Get the stock photo for current event type
  const currentStockPhoto = getEventCover(formData.event_type, null);

  // If Paid is turned on, automatically require RSVP
  useEffect(() => {
    if (formData.is_paid && !formData.requires_rsvp) {
      setFormData(prev => ({ ...prev, requires_rsvp: true }));
    }
  }, [formData.is_paid]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      let image_url;
      
      if (!useStockImage && file) {
        // Process and upload custom image
        const processed = await processImage(file);
        const uploadForm = new FormData();
        uploadForm.append('file', processed, 'event.jpg');
        
        const uploadResponse = await api.post('/api/calendar/upload-event-image', uploadForm, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        image_url = uploadResponse.data.image_url;
      } else {
        // Use stock image
        image_url = null; // Will use default stock image based on event type
      }

      // Get HOA timezone from settings
      const hoaSettings = await api.get('/api/hoa-settings').catch(err => {
        console.warn('HOA settings fetch failed, using defaults:', err);
        return { data: { success: true, hoa_settings: { calendar: { board_only_create: true } } } };
      });
      const hoaTimezone = hoaSettings.data?.timezone || 'America/Chicago'; // Default to Central
      
      console.log('🕐 HOA Timezone:', hoaTimezone);
      console.log('📅 Raw form datetime:', formData.start_datetime);
      
      // The datetime-local input gives us "2024-10-27T18:00" without timezone
      // When we do new Date(formData.start_datetime), JavaScript interprets it in the BROWSER's timezone
      // This causes issues if the browser timezone != HOA timezone
      
      // Solution: Interpret the datetime string as if it's in the HOA timezone
      // For Central Time (UTC-5 or UTC-6 depending on DST):
      // If user enters "2024-10-27T18:00", we want to store "2024-10-27T23:00:00Z" (6pm Central = 11pm UTC in CDT)
      // But JavaScript will interpret "2024-10-27T18:00" in browser timezone first
      
      // Best approach: Send the raw datetime string and let backend know the intended timezone
      // But since Pydantic expects datetime, we need to send ISO string
      // Workaround: Convert assuming browser IS in HOA timezone (user must be in correct timezone)
      
      const eventData = {
        title: formData.title,
        description: formData.description || null,
        location: formData.location || null,
        start_datetime: new Date(formData.start_datetime).toISOString(),
        end_datetime: new Date(formData.end_datetime).toISOString(),
        all_day: formData.all_day,
        event_type: formData.event_type, // Already mapped to enum value
        visibility: formData.visibility,
        requires_rsvp: formData.requires_rsvp,
        max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : null,
        image_url,
        is_paid: formData.is_paid,
        price: formData.is_paid && formData.price ? parseFloat(formData.price) : null,
        // Normalize RSVP fields for backend model
        rsvp_deadline_type: formData.rsvp_deadline_type,
        rsvp_custom_days:
          formData.rsvp_deadline_type === 'custom' ? null : (formData.rsvp_custom_days ?? null),
        rsvp_deadline:
          formData.rsvp_deadline_type === 'custom' && formData.rsvp_deadline
            ? new Date(formData.rsvp_deadline).toISOString()
            : null,
        // Recurring event fields
        is_recurring: formData.is_recurring,
        recurrence_type: formData.recurrence_type || 'none',
        recurrence_interval: formData.recurrence_interval || 1,
        recurrence_end_date: formData.recurrence_end_date && formData.recurrence_end_date.trim() 
          ? new Date(formData.recurrence_end_date).toISOString() 
          : null,
        recurrence_count: formData.recurrence_count || null
      };
      
      console.log('Creating event with data:', eventData);
      console.log('API baseURL:', api.defaults.baseURL);
      console.log('Full URL will be:', api.defaults.baseURL + '/api/calendar/events');

      const response = await api.post('/api/calendar/events', eventData);
      if (response.data?.success !== false) {
        // Store created event
        const newEvent = response.data.event || { id: response.data.event_id };
        setCreatedEvent(newEvent);
        
        // If "Add Agenda Now" was selected, open agenda modal
        if (agendaOption === 'add_now') {
          setShowAgendaModal(true);
          // Don't close this modal yet, keep it open behind agenda modal
        } else {
          onSaved();
        }
      }
    } catch (error) {
      console.error('Error creating event:', error);
      const errorDetail = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      alert(`Failed to create event: ${errorDetail}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-xl text-gray-900">Create Event</h3>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input 
              type="text"
              name="title"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={formData.title}
              onChange={e => setFormData({...formData, title: e.target.value})}
              placeholder="Event Title"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={formData.event_type}
              onChange={e => setFormData({...formData, event_type: e.target.value})}
            >
              {EVENT_TYPES.map(type => (
                <option key={type} value={EVENT_TYPE_MAPPING[type]}>{type}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={formData.visibility}
              onChange={e => setFormData({...formData, visibility: e.target.value})}
            >
              <option value="public">All Homeowners</option>
              <option value="board">Board Only</option>
              <option value="committee">Committee</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date & Time</label>
            <input 
              type="datetime-local"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={formData.start_datetime}
              onChange={e => {
                const startTime = e.target.value;
                const endTime = new Date(new Date(startTime).getTime() + 3600000).toISOString().slice(0, 16);
                setFormData({...formData, start_datetime: startTime, end_datetime: endTime});
              }}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date & Time</label>
            <input 
              type="datetime-local"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={formData.end_datetime}
              onChange={e => setFormData({...formData, end_datetime: e.target.value})}
            />
          </div>
          
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input 
              type="text"
              name="location"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={formData.location}
              onChange={e => setFormData({...formData, location: e.target.value})}
              placeholder="Community Center, Pool Area, etc."
            />
          </div>
          
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-[100px]"
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              placeholder="Event details, what to bring, etc."
            />
          </div>
          
          <div className="col-span-2">
            <div className="flex flex-wrap gap-4 items-center">
              <label className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={formData.all_day}
                  onChange={e => setFormData({...formData, all_day: e.target.checked})}
                />
                <span className="text-sm">All day</span>
              </label>
              <label className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={formData.requires_rsvp}
                  onChange={e => setFormData({...formData, requires_rsvp: e.target.checked})}
                />
                <span className="text-sm">Require RSVP</span>
              </label>

              {(formData.requires_rsvp || formData.is_paid) && (
                <div className="col-span-3 mt-3 space-y-2">
                  <label className="block text-sm font-medium text-gray-700">RSVP Deadline</label>
                  <div className="grid md:grid-cols-2 gap-3">
                    <select
                      className="border rounded px-3 py-2"
                      value={formData.rsvp_deadline_type}
                      onChange={e => setFormData(prev => ({ ...prev, rsvp_deadline_type: e.target.value }))}
                    >
                      <option value="until_event">Until event start</option>
                      <option value="1_day_before">1 day before</option>
                      <option value="2_days_before">2 days before</option>
                      <option value="1_week_before">1 week before</option>
                      <option value="custom">Custom date/time</option>
                    </select>

                    {formData.rsvp_deadline_type === 'custom' ? (
                      <input
                        type="datetime-local"
                        className="border rounded px-3 py-2"
                        value={formData.rsvp_deadline}
                        onChange={e => setFormData(prev => ({ ...prev, rsvp_deadline: e.target.value }))}
                      />
                    ) : (
                      <input
                        type="number"
                        min="1"
                        placeholder="Custom days before (optional)"
                        className="border rounded px-3 py-2"
                        value={formData.rsvp_custom_days ?? ''}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          rsvp_custom_days: e.target.value ? parseInt(e.target.value, 10) : null
                        }))}
                      />
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    If Paid is selected, RSVP is automatically required so you can manage a guest list and payments.
                  </p>
                </div>
              )}
              <label className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={formData.is_recurring}
                  onChange={e => setFormData({...formData, is_recurring: e.target.checked})}
                />
                <span className="text-sm">Recurring Event</span>
              </label>
              <label className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={formData.is_paid}
                  onChange={e => setFormData({...formData, is_paid: e.target.checked})}
                />
                <span className="text-sm">Paid Event</span>
              </label>
            </div>
          </div>

          {/* Paid Event Price Field */}
          {formData.is_paid && (
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Price ($)</label>
              <input 
                type="number"
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={formData.price}
                onChange={e => setFormData({...formData, price: e.target.value})}
                placeholder="25.00"
              />
            </div>
          )}
          
          {/* Recurring Event Options */}
          {formData.is_recurring && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recurrence Type</label>
                <select 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={formData.recurrence_type}
                  onChange={e => setFormData({...formData, recurrence_type: e.target.value})}
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Repeat Every</label>
                <div className="flex items-center gap-2">
                  <input 
                    type="number"
                    min="1"
                    max="12"
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={formData.recurrence_interval}
                    onChange={e => setFormData({...formData, recurrence_interval: parseInt(e.target.value)})}
                  />
                  <span className="text-sm text-gray-600">
                    {formData.recurrence_type === 'weekly' && 'week(s)'}
                    {formData.recurrence_type === 'monthly' && 'month(s)'}
                    {formData.recurrence_type === 'yearly' && 'year(s)'}
                  </span>
                </div>
              </div>
              
              {formData.recurrence_type === 'monthly' && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Pattern</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input 
                        type="radio" 
                        name="monthly_pattern"
                        value="day_of_month"
                        checked={!formData.recurrence_pattern || formData.recurrence_pattern === 'day_of_month'}
                        onChange={e => setFormData({...formData, recurrence_pattern: 'day_of_month'})}
                      />
                      <span className="text-sm">Same day of month (e.g., 15th of every month)</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input 
                        type="radio" 
                        name="monthly_pattern"
                        value="day_of_week"
                        checked={formData.recurrence_pattern === 'day_of_week'}
                        onChange={e => setFormData({...formData, recurrence_pattern: 'day_of_week'})}
                      />
                      <span className="text-sm">Same day of week (e.g., third Thursday)</span>
                    </label>
                  </div>
                  
                  {formData.recurrence_pattern === 'day_of_week' && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <select 
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        value={formData.recurrence_week_of_month}
                        onChange={e => setFormData({...formData, recurrence_week_of_month: e.target.value})}
                      >
                        <option value="">Select Week</option>
                        <option value="first">First</option>
                        <option value="second">Second</option>
                        <option value="third">Third</option>
                        <option value="fourth">Fourth</option>
                        <option value="last">Last</option>
                      </select>
                      <select 
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        value={formData.recurrence_day_of_week}
                        onChange={e => setFormData({...formData, recurrence_day_of_week: e.target.value})}
                      >
                        <option value="">Select Day</option>
                        <option value="monday">Monday</option>
                        <option value="tuesday">Tuesday</option>
                        <option value="wednesday">Wednesday</option>
                        <option value="thursday">Thursday</option>
                        <option value="friday">Friday</option>
                        <option value="saturday">Saturday</option>
                        <option value="sunday">Sunday</option>
                      </select>
                    </div>
                  )}
                </div>
              )}
              
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">End Recurrence</label>
                <input 
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={formData.recurrence_end_date}
                  onChange={e => setFormData({...formData, recurrence_end_date: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for indefinite recurrence</p>
              </div>
            </>
          )}
          
          {/* Image Options */}
          <div className="col-span-2">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Event Image</h4>
            
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input 
                  type="radio" 
                  name="image_option"
                  checked={useStockImage}
                  onChange={() => setUseStockImage(true)}
                />
                <span className="text-sm">Use stock photo for event type</span>
              </label>
              
              {useStockImage && (
                <div className="ml-6 p-3 bg-gray-50 rounded-lg">
                  <img 
                    src={currentStockPhoto}
                    alt="Stock photo preview"
                    className="w-24 h-16 object-cover rounded-lg mb-2"
                    onError={(e) => {
                      e.target.src = `/assets/events/stock/${STOCK_EVENT_COVERS.other}`;
                    }}
                  />
                  <p className="text-xs text-gray-600">Preview for {REVERSE_EVENT_TYPE_MAPPING[formData.event_type] || 'Other'} event type</p>
                </div>
              )}
              
              <label className="flex items-center gap-2">
                <input 
                  type="radio" 
                  name="image_option"
                  checked={!useStockImage}
                  onChange={() => setUseStockImage(false)}
                />
                <span className="text-sm">Upload custom image</span>
              </label>
              
              {!useStockImage && (
                <div className="ml-6">
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={e => setFile(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Image will be automatically resized and watermarked</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Agenda Options for BOD/ARC Meetings */}
        {isMeetingType && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800 font-medium mb-3">
              💡 Tip: Board and ARC meetings can include an agenda. You can add it now or later.
            </p>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="agendaOption"
                  value="no_agenda"
                  checked={agendaOption === 'no_agenda'}
                  onChange={(e) => setAgendaOption(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">No Agenda (Default)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="agendaOption"
                  value="add_later"
                  checked={agendaOption === 'add_later'}
                  onChange={(e) => setAgendaOption(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Add Agenda Later (Will send reminder)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="agendaOption"
                  value="add_now"
                  checked={agendaOption === 'add_now'}
                  onChange={(e) => setAgendaOption(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Add Agenda Now</span>
              </label>
            </div>
          </div>
        )}
        
        <div className="flex gap-3 mt-6">
          <button 
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            disabled={saving || !formData.title}
            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors"
          >
            {saving ? 'Creating...' : 'Create Event'}
          </button>
        </div>
      </div>
      
      {/* Agenda Modal - shown after event creation if "Add Now" selected */}
      {showAgendaModal && createdEvent && (
        <AgendaModal
          event={createdEvent}
          onClose={() => {
            setShowAgendaModal(false);
            onSaved(); // Close parent form and refresh
          }}
          onSaved={() => {
            // Agenda saved, can close both modals
          }}
        />
      )}
    </div>
  );
}

// Agenda Management Modal Component
function AgendaModal({ event, onClose, onSaved }) {
  const [agendaItems, setAgendaItems] = useState([
    { id: '1', order: 1, title: 'Call to Order', notes: '', is_editable: true },
    { id: '2', order: 2, title: 'Certification of Quorum', notes: '', is_editable: true },
    { id: '3', order: 3, title: 'Proof of Notice of Meeting', notes: '', is_editable: true },
    { id: '4', order: 4, title: 'Approval of prior meeting minutes', notes: '', is_editable: true },
    { id: '5', order: 5, title: 'Old Business', notes: '', is_editable: true },
    { id: '6', order: 6, title: 'New Business', notes: '', is_editable: true },
    { id: '7', order: 7, title: 'Open to Floor', notes: '', is_editable: true },
    { id: '8', order: 8, title: 'Adjourn', notes: '', is_editable: true }
  ]);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  
  // Load existing agenda if any
  useEffect(() => {
    if (event?.id) {
      loadAgenda();
    }
  }, [event?.id]);
  
  const loadAgenda = async () => {
    try {
      const response = await api.get(`/api/calendar/events/${event.id}/agenda`);
      if (response.data.success && response.data.agenda && response.data.agenda.items) {
        setAgendaItems(response.data.agenda.items);
      }
    } catch (error) {
      console.error('Error loading agenda:', error);
    }
  };
  
  const handleItemChange = (id, field, value) => {
    setAgendaItems(items =>
      items.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };
  
  const addItem = () => {
    const newOrder = Math.max(...agendaItems.map(i => i.order), 0) + 1;
    setAgendaItems([...agendaItems, {
      id: Date.now().toString(),
      order: newOrder,
      title: 'New Item',
      notes: '',
      is_editable: true
    }]);
  };
  
  const deleteItem = (id) => {
    setAgendaItems(items => items.filter(i => i.id !== id));
  };
  
  const moveItem = (index, direction) => {
    const newItems = [...agendaItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    // Update order numbers
    newItems.forEach((item, idx) => {
      item.order = idx + 1;
    });
    setAgendaItems(newItems);
  };
  
  const saveAgenda = async (finalize = false) => {
    setSaving(true);
    try {
      console.log('💾 Saving agenda for event:', event);
      console.log('📝 Event ID:', event?.id);
      console.log('📋 Agenda items:', agendaItems);
      
      if (!event?.id) {
        throw new Error('Event ID is missing. Cannot save agenda.');
      }
      
      await api.post(`/api/calendar/events/${event.id}/agenda`, {
        items: agendaItems,
        status: finalize ? 'finalized' : 'draft'
      });
      
      alert(finalize ? 'Agenda finalized!' : 'Agenda saved as draft');
      if (onSaved) onSaved();
      if (finalize) onClose();
    } catch (error) {
      console.error('❌ Error saving agenda:', error);
      console.error('Error details:', error.response?.data);
      alert('Failed to save agenda: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };
  
  const emailAgenda = async () => {
    if (!confirm('Email this agenda to all homeowners?')) return;
    
    setEmailing(true);
    try {
      console.log('📧 Emailing agenda for event:', event);
      console.log('📝 Event ID:', event?.id);
      
      if (!event?.id) {
        throw new Error('Event ID is missing. Cannot email agenda.');
      }
      
      // FIRST: Save the agenda if it hasn't been saved yet
      console.log('💾 Saving agenda before emailing...');
      const agendaPayload = {
        items: agendaItems.map((item, index) => ({
          id: item.id || `item-${index}`,
          order: index + 1,
          title: item.title,
          notes: item.notes || '',
          duration_minutes: item.duration_minutes || null,
          is_editable: true
        })),
        status: 'draft'
      };
      
      // Save/update the agenda
      await api.post(`/api/calendar/events/${event.id}/agenda`, agendaPayload);
      console.log('✅ Agenda saved successfully');
      
      // THEN: Email it
      console.log('📧 Now emailing the saved agenda...');
      const response = await api.post(`/api/calendar/events/${event.id}/agenda/email`);
      alert(response.data.message || 'Agenda emailed successfully!');
      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      console.error('❌ Error emailing agenda:', error);
      console.error('Error details:', error.response?.data);
      alert('Failed to email agenda: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEmailing(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Only close if clicking the backdrop itself, not the modal content
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold">Meeting Agenda</h3>
          <button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }} 
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        
        <div className="p-6">
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            💡 Tip: Add notes to items #4, #5, and #6. You can add, remove, or reorder items as needed.
          </div>
          
          <div className="space-y-3">
            {agendaItems.map((item, index) => (
              <div key={item.id} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-start gap-3">
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        moveItem(index, 'up');
                      }}
                      disabled={index === 0}
                      className="text-xs px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        moveItem(index, 'down');
                      }}
                      disabled={index === agendaItems.length - 1}
                      className="text-xs px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex gap-2 mb-2">
                      <span className="text-sm font-semibold text-gray-600">{item.order}.</span>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => handleItemChange(item.id, 'title', e.target.value)}
                        className="flex-1 px-3 py-1 border rounded-md"
                        placeholder="Item title"
                      />
                      {item.is_editable && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            deleteItem(item.id);
                          }}
                          className="px-3 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                    
                    {[4, 5, 6].includes(item.order) && (
                      <textarea
                        value={item.notes}
                        onChange={(e) => handleItemChange(item.id, 'notes', e.target.value)}
                        className="w-full px-3 py-2 border rounded-md text-sm"
                        placeholder="Add notes/comments..."
                        rows="2"
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              addItem();
            }}
            className="mt-4 w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700"
          >
            + Add Item
          </button>
        </div>
        
        <div className="sticky bottom-0 bg-white border-t p-4 flex gap-3">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              saveAgenda(false);
            }}
            disabled={saving || emailing}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              saveAgenda(true);
            }}
            disabled={saving || emailing}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Finalize
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              emailAgenda();
            }}
            disabled={saving || emailing}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            {emailing ? 'Emailing...' : '📧 Email Agenda'}
          </button>
        </div>
      </div>
    </div>
  );
}